import React from 'react';
import FacilityStore from '../../stores/FacilityStore';
import ViewerStore from '../../stores/ViewerStore';
import AssemblyLibraryStore from '../../stores/AssemblyLibraryStore';
import { SplitterComponent, PanesDirective, PaneDirective } from '@syncfusion/ej2-react-layouts';
import React3DViewer from '../lodviewer/ReactViewer3D';
import AssetInformation from '../components/AssetInformation';
import ViewerActions from '../../actions/ViewerActions';
import AssetActions from '../../actions/AssetActions';
import AssetSetsActions from '../../actions/AssetSetsActions';
import AssetTagsBar from '../../components/AssetTagsBar';
import MeasurementBar from '../../components/MeasurementBar';
import AssemblyLibraryBar from '../../components/AssemblyLibraryBar';
import { ContentLayers, IHierarchyItemDetail } from '../../Interfaces/ViewerInterfaces';
import NavigationPopup from '../../components/NavigationPopup';
import ReactViewer3DActions from '../lodviewer/ReactViewer3DActions';
import AssetTagsForm from '../components/AssetTagsForm';
import { useUpdateEffect } from '../../hooks/useUpdateEffect';
import FacilityMiniMap from '../../components/FacilityMiniMap';
import OntologyDropDown from '../../components/OntologyDropDown';
import KnowledgeBookStore from '../../stores/KnowledgeBookStore';
import ContextMenu from '../components/ContextMenu';
import AnnotationLinkEditor from '../components/AnnotationLinkEditor';
import AnnotationTextEditor from '../components/AnnotationTextEditor';
import AnnotationEditor from '../components/AnnotationEditor';
import * as Inovx from '@shared';
import ContentLayersButton from './ContentLayersButton';
import AssetInformationButton from './AssetInformationButton';
import ComponentHelpers from '../../helpers/ComponentHelpers';
import AssetSetVisualizationStore from '../../stores/AssetSetVisualizationStore';
import {
    type IAssetSetDisplayDefinition,
    buildAssetSetDisplayDefinitions,
    GOLDEN_DISPLAY_COLOR,
    normalizeDisplayID,
} from '../../helpers/assetSetVisualizationModel';
import {
    areViewerAssetSetsResolved,
    canModifyAssetSetMembership,
    getAssetSetDefinitionSignature,
    getViewerGeometryContextSignature,
    resolveViewerAssetSets,
    shouldUseKnowledgeBookAssetSetManageMode
} from '../../helpers/viewerAssetSetModel';
import { getApplicableFacilityAssetSets } from '../../helpers/facilityAssetSetOntologyFilter';
import ViewerHelpers from '../../helpers/ViewerHelpers';
import { isBlackAndWhiteScanPosition, isScanPositionView } from '../../helpers/viewMedia';
import { shouldMark3DCameraDirty } from '../../helpers/knowledgeViewCameraState';
import {
    getRepeatedSelectionBehavior,
    shouldPreserveSelectionForContextClick
} from '../../helpers/viewerSelectionModel';
import EditAssemblyForm from '../components/EditAssemblyForm';
import * as Interfaces from '../../Interfaces/Interfaces';
import * as Enumerations from '../../Enumerations/Enumerations';
const { useState, useEffect, useRef } = React;
var SystemActions = Inovx.Shared.System.Actions;
interface LODViewerProps { }
type VolumeChangeMode =
    | 'tagmove'
    | 'tagscale'
    | 'assemblymove'
    | 'assemblyscale';

const USER_CAMERA_INTERACTION_WINDOW_MS = 2000;

const LODViewer = React.forwardRef((props: LODViewerProps, ref) => {
    const outerSplitterInstance = useRef<SplitterComponent | null>(null);
    const propertySplitterInstance = useRef<SplitterComponent | null>(null);
    const viewerShellRef = useRef<HTMLDivElement | null>(null);
    const panoContainerRef = useRef<HTMLDivElement | null>(null);
    const viewerId = useRef<string>('Viewer-' + new Date().getTime());
    const pendingResizeTimeoutsRef = useRef<number[]>([]);
    const knowledgeViewAssetIsolationActiveRef = useRef<boolean>(!!ViewerStore.getSelectedKnowledgeView());
    const knowledgeViewAssetSetSetupRef = useRef<{
        knowledgeViewID: string | null;
        nonAllSignature: string;
        allSetID: string | null;
        allLayerMapped: boolean;
    }>({
        knowledgeViewID: null,
        nonAllSignature: '',
        allSetID: null,
        allLayerMapped: false,
    });
    // Mounted guard
    const mountedRef = React.useRef(true);
    React.useEffect(() => () => { mountedRef.current = false; }, []);

    React.useEffect(() => {
        const viewerShell = viewerShellRef.current;
        if (!viewerShell) return;

        const preventNativeContextMenu = (event: MouseEvent) => {
            event.preventDefault();
        };

        viewerShell.addEventListener('contextmenu', preventNativeContextMenu, true);
        return () => viewerShell.removeEventListener('contextmenu', preventNativeContextMenu, true);
    }, []);

    const [openedKnowledgeBook, setOpenedKnowledgeBook] = useState<IKnowledgeBook>(KnowledgeBookStore.getSelectedKnowledgeBook());
    const [knowledgeBookViewingMode, setKnowledgeBookViewingMode] = useState<KnowledgeBookViewingMode>(KnowledgeBookStore.getKnowledgeBookViewingMode());
    const [facilityAssetSetsBarOpen, setFacilityAssetSetsBarOpen] =
        useState<boolean>(FacilityStore.getFacilityAssetSetsBarOpen());
    const [assetSets, setAssetSets] = useState<IAssetSet[]>(KnowledgeBookStore.getAllAssetSet());
    const [allFacilityAssetSets, setAllFacilityAssetSets] = useState<IAssetSet[]>([]);
    const [visualizationConfiguration, setVisualizationConfiguration] =
        useState<IAssetSetDisplayConfiguration | null>(AssetSetVisualizationStore.getConfiguration());
    const [selectedFacilityID, setSelectedFacilityID] =
        useState<string | null>(FacilityStore.getSelectedFacility()?.ID ?? null);
    const [knowledgeBook, setKnowledgeBook] = useState<IKnowledgeBook>(KnowledgeBookStore.getKnowledgeBook());
    const [selectedAsset, setSelectedAsset] = useState<string>(ViewerStore.getSelectedAsset());
    const [allowMultiSelect, setAllowMultiSelect] = useState<boolean>(ViewerStore.getAllowMultiSelect());
    const [selectedAnnotation, setSelectedAnnotation] = useState<string>(ViewerStore.getSelectedAnnotation());
    const [selectedHierarchyItem, setSelectedHierarchyItem] = useState<IHierarchyItemDetail>(ViewerStore.getSelectedHierarchyItem());
    const [focusedHierarchyItem, setFocusedHierarchyItem] = useState<{ id: string }>(ViewerStore.getFocusedHierarchyItemID());
    const [selectedMeshItem, setSelectedMeshItem] = useState<string>(ViewerStore.getSelectedMeshItem());
    const [showSplitter, setShowSplitter] = useState<boolean>(false);
    const [isAssetPanelVisible, setAssetPanelVisible] = useState<boolean>(ViewerStore.getAssetPanelVisibility());
    const [renderMiniMap, setRenderMiniMap] = useState<boolean>(ViewerStore.isMiniMapOpen());
    const [viewerConfig, setViewerConfig] = useState<IFacilityConfig>(ViewerStore.getSettings());
    const [documentLink, setDocumentLink] = useState<string>(null);
    const [rangePosition, setRangePosition] = useState<IVector3D>(ViewerStore.getRangePosition());
    const [viewerSettings, setViewerSettings] = useState<Record<string, any>>(ViewerStore.getViewerRuntimeSettings());

    const [selectedKnowledgeView, setSelectedKnowledgeView] = useState<IKnowledgeViewDetail | null>(ViewerStore.getSelectedKnowledgeView());
    const [selectedAssetSet, setSelectedAssetSet] = useState<IAssetSet | null>(ViewerStore.getSelectedAssetSet());

    const [measurements, setMeasurements] = useState<Measurement[]>(ViewerStore.getMeasurements());
    const [displayMode, setDisplayMode] = useState<DisplayMode>(ViewerStore.getDisplayMode());
    const [measurementType, setMeasurementType] = useState<MeasurementType>(ViewerStore.getMeasurementType());
    const [focusedMeasurement, setFocusedMeasurement] = useState<Measurement>(ViewerStore.getFocusedMeasurement());

    const [allAssetTags, setAllAssetTags] = useState<IAssetTag[]>(FacilityStore.getSelectedFacility().AssetTags);
    const [filteredAssets, setFilteredAssets] = useState<string[]>(ViewerStore.getFilteredAssets());
    const [visibleAssetTagClasses, setVisibleAssetTagClasses] = useState<string[] | null>(ViewerStore.getVisibleAssetTagClasses());
    const [focusedAnnotation, setFocusedAnnotation] = useState<IAnnotationDisplayItem>(ViewerStore.getFocusedAnnotation());
    const [displayObstructedTags, setDisplayObstructedTags] = useState<boolean>(ViewerStore.getDisplayObstructedTags());

    const [facilityTitle] = useState<string>(FacilityStore.getSelectedFacility().Name);
    const [siteMap, setSiteMap] = useState<ISiteMap>(getSiteMap());
    const [views, setViews] = useState<IView360Detail[]>(FacilityStore.getSelectedFacility().Views);
    const [directionDegrees, setDirectionDegrees] = useState(0);
    const [overridePosition, setOverridePosition] = useState<IVector3D | null>(null);
    const [highlightedView, setHighlightedView] = useState<IView360Detail | null>(null);
    const [scanContextMenu, setScanContextMenu] = useState<{ visible: boolean; left: number; top: number; view: IView360Detail | null }>({ visible: false, left: -1000, top: -1000, view: null });

    const [assemblies, setAssemblies] = useState<IAssembly[]>(ViewerStore.getAssemblies());
    const [assemblyTemplates, setAssemblyTemplates] = useState<IAssemblyTemplate[]>(AssemblyLibraryStore.getAllAssemblyTemplates());
    const [assemblyCreationTemplate, setAssemblyCreationTemplate] = useState<IAssemblyTemplate>(ViewerStore.getAssemblyCreationTemplate());
    const [selectedAssembly, setSelectedAssembly] = useState<string>(ViewerStore.getSelectedAssembly());

    const [assetTagContextMenu, setAssetTagContextMenu] = useState<{ visible: boolean; left: number; top: number; tagId: string | null; items: any }>({ visible: false, left: -1000, top: -1000, tagId: null, items: [] });

    const [assemblyContextMenu, setAssemblyContextMenu] = useState<{ visible: boolean; left: number; top: number; tagId: string | null }>({ visible: false, left: -1000, top: -1000, tagId: null });

    const [contentLayers, setContentLayers] = useState<ContentLayers>(ViewerStore.getContentLayers());

    const [selectedOntology, setSelectedOntology] = useState<IOntologyPart>(
        FacilityStore.getSelectedOntology() ?? FacilityStore.getRootOntologyPart()
    );
    const [selectedOntologyTypes, setSelectedOntologyTypes] = useState<string[]>(FacilityStore.getSelectedOntologyPartContentTypes());
    const facilityAssetSets = React.useMemo(
        () => getApplicableFacilityAssetSets(allFacilityAssetSets, selectedOntology?.ID),
        [allFacilityAssetSets, selectedOntology?.ID]
    );

    const [contextMenu, setContextMenu] = useState<boolean>(false);
    const [lastAnnotationPosition, setLastAnnotationPosition] = useState({ x: 0, y: 0 });
    const [isAnnotationTextShow, setIsAnnotationTextShow] = useState(false);
    const [isAnnotationDetailShow, setIsAnnotationDetailShow] = useState(false);
    const [isAnnotationLinkDetailShow, setIsAnnotationLinkDetailShow] = useState(false);
    const [isEditAssemblyModalVisible, setIsEditAssemblyModalVisible] = useState(false);
    const [selectedAssemblyObj, setSelectedAssemblyObj] = useState<IAssembly>(null);
   
    //modify and manage tags permissions
    const hasModifyTagsPermission = React.useMemo(() => { return ComponentHelpers.hasModifyTagsPermission(); }, []);
    const hasManageTagsPermission = React.useMemo(() => { return ComponentHelpers.hasManageTagsPermission(); }, []);
    const hasManageContentPermission = React.useMemo(() => { return ComponentHelpers.hasManageContentPermission(); }, []);

    const hasModifyKBPermission = React.useMemo(() => ComponentHelpers.hasModifyKnowledgeBookPermission(openedKnowledgeBook), [openedKnowledgeBook]);
    const hasManageKBPermission = React.useMemo(() => ComponentHelpers.hasManageKnowledgeBookPermission(openedKnowledgeBook), [openedKnowledgeBook]);

    const hasOntologyHierarchy =
        (FacilityStore.getSelectedFacility()?.OntologyParts?.length || 0) > 1;


    const getKVTagsInfo = (): IKVTagsInfo => {
        return {
            tags: ViewerStore.getSelectedKnowledgeView()?.Tags,
            version: ViewerStore.getTagsVersion()
        }
    }

    const [kvTagsInfo, setKvTagsInfo] = useState<IKVTagsInfo>(getKVTagsInfo());

    interface IKVTagsInfo {
        tags: IViewAnnotationTag[];
        version: number;
    }

    useEffect(() => {
        ViewerActions.setViewerId(viewerId.current);
    }, []);

    const handleKnowledgeBookStoreChange = React.useCallback(() => {
        if (!mountedRef.current) return;
        setOpenedKnowledgeBook(KnowledgeBookStore.getSelectedKnowledgeBook());
        setKnowledgeBookViewingMode(KnowledgeBookStore.getKnowledgeBookViewingMode());
        setKnowledgeBook(KnowledgeBookStore.getKnowledgeBook());
        setAssetSets(KnowledgeBookStore.getAllAssetSet());
    }, []);

    const handleViewerStoreChange = React.useCallback(() => {
        if (!mountedRef.current) return;
        addTooltipForTruncatedText();
        setSelectedKnowledgeView(ViewerStore.getSelectedKnowledgeView());
        setSelectedAssetSet(ViewerStore.getSelectedAssetSet());
        setAllowMultiSelect(ViewerStore.getAllowMultiSelect());
        setSelectedAsset(ViewerStore.getSelectedAsset());
        setSelectedAnnotation(ViewerStore.getSelectedAnnotation());
        setAssetPanelVisible(ViewerStore.getAssetPanelVisibility());
        setSelectedHierarchyItem(ViewerStore.getSelectedHierarchyItem());
        setFocusedHierarchyItem(ViewerStore.getFocusedHierarchyItemID());
        setSelectedMeshItem(ViewerStore.getSelectedMeshItem());
        setRangePosition(ViewerStore.getRangePosition());
        setViewerSettings(ViewerStore.getViewerRuntimeSettings());
        setDisplayMode(ViewerStore.getDisplayMode());
        setMeasurementType(ViewerStore.getMeasurementType());
        setMeasurements(ViewerStore.getMeasurements());
        setKvTagsInfo(prevState => {
            const newState = getKVTagsInfo();
            return (newState.version != prevState.version) ? newState : prevState;
        });
        setFocusedMeasurement(ViewerStore.getFocusedMeasurement());
        setFilteredAssets(ViewerStore.getFilteredAssets());
        setVisibleAssetTagClasses(ViewerStore.getVisibleAssetTagClasses());
        setFocusedAnnotation(ViewerStore.getFocusedAnnotation());
        setAssemblies(ViewerStore.getAssemblies());
        setAssemblyCreationTemplate(ViewerStore.getAssemblyCreationTemplate());
        setSelectedAssembly(ViewerStore.getSelectedAssembly());
        setViewerConfig(ViewerStore.getSettings());
        setContentLayers(ViewerStore.getContentLayers());
        setDisplayObstructedTags(ViewerStore.getDisplayObstructedTags());
        setRenderMiniMap(current => current || ViewerStore.isMiniMapOpen());
    }, []);

    const handleFacilityStoreChange = React.useCallback(() => {

        if (!mountedRef.current) return;
        setSiteMap(getSiteMap());
        setAllAssetTags(FacilityStore.getSelectedFacility().AssetTags);
        setViews(FacilityStore.getSelectedFacility().Views);
        setSelectedAnnotation(ViewerStore.getSelectedAnnotation());
        const newSelectedOntology = FacilityStore.getSelectedOntology() ?? FacilityStore.getRootOntologyPart();
        setSelectedOntology(newSelectedOntology);
        setSelectedOntologyTypes(FacilityStore.getSelectedOntologyPartContentTypes());
        const facilityAssetSetBarOpen = FacilityStore.getFacilityAssetSetsBarOpen();
        setFacilityAssetSetsBarOpen(facilityAssetSetBarOpen);
        setSelectedFacilityID(FacilityStore.getSelectedFacility()?.ID ?? null);
    }, []);

    const handleAssetSetVisualizationStoreChange = React.useCallback(() => {
        if (!mountedRef.current) return;
        setVisualizationConfiguration(AssetSetVisualizationStore.getConfiguration());
    }, []);

    const handleVisualizationAssetSetsChange = React.useCallback((facilityID: string, assetSetID?: string) => {
        if (!mountedRef.current || facilityID !== FacilityStore.getSelectedFacility()?.ID) return;
        if (assetSetID) {
            ViewerActions.invalidateAssetSetMembership(assetSetID);
        } else {
            ViewerActions.invalidateAllAssetSetMemberships();
        }
        void AssetSetsActions.refreshFacilityAssetSets(facilityID, false).then(sets => {
            setAllFacilityAssetSets(sets ?? []);
            const facilitySets = (sets ?? []).filter(assetSet => !assetSet.KnowledgeBookID);
            const all3DAssets = facilitySets.find(assetSet => assetSet.Type === AssetSetType.All3DAssets);
            const allAssetTags = facilitySets.find(assetSet => assetSet.Type === AssetSetType.AllAssetTags);
            if (all3DAssets && allAssetTags) {
                AssetSetVisualizationStore.reconcile(facilityID, facilitySets, all3DAssets.ID, allAssetTags.ID);
            }
            setAssetSetMembershipRevision(revision => revision + 1);
        });
    }, []);

    const allowedCoreKeys = React.useMemo(() => {
        return ViewerActions.getCoreKeys(selectedOntologyTypes);
    }, [selectedOntologyTypes]);


    React.useEffect(() => {
        ReactViewer3DActions.setViewerSettings(viewerId.current, viewerSettings);
    }, [viewerSettings]);

    React.useEffect(() => {
        // Only set the content layers if we're not in a KV. Otherwise, the KV drives which content layers are set.
        if (!selectedKnowledgeView) {
            const current = ViewerStore.getContentLayers();
            const next = { ...current };

            const hasAssets = allowedCoreKeys.includes('assets');
            const hasCad = allowedCoreKeys.includes('cad');
            const hasPoint = allowedCoreKeys.includes('pointCloud');
            const hasMesh = allowedCoreKeys.includes('mesh');

            if (!current.pointCloud) {
                next.assets = hasAssets;
                next.cad = hasCad;
                next.pointCloud = !hasAssets && !hasCad && !hasMesh && hasPoint;
                next.mesh = hasMesh;
            }

            if (next.assets !== current.assets || next.cad !== current.cad || next.pointCloud !== current.pointCloud) {
                Promise.resolve().then(() => ViewerActions.setContentLayers(next));
            }
        }
    }, [allowedCoreKeys, selectedKnowledgeView]);

    useEffect(() => {
        ReactViewer3DActions.setContentLayers(viewerId.current, contentLayers);
    }, [contentLayers]);

    /*
    useEffect(() => {     
        // SS comment pending... i don't think this is neccessary anymore, but will leave it in.
        // the viewer's direction/position gets initialized optionally based on a dir/pos, which is now set
        // on these display properties below.
        if (selectedKnowledgeView && !isAssetSetManageMode) {
            if (selectedKnowledgeView?.DisplayProperties?.CameraPosition && selectedKnowledgeView?.DisplayProperties?.CameraDirection) {
                const displayProperties = selectedKnowledgeView.DisplayProperties;
                ReactViewer3DActions.setCamera(viewerId.current, new BABYLON.Vector3(
                    displayProperties.CameraPosition.X,
                    displayProperties.CameraPosition.Y,
                    displayProperties.CameraPosition.Z
                ), new BABYLON.Vector3(
                    displayProperties.CameraDirection.X,
                    displayProperties.CameraDirection.Y,
                    displayProperties.CameraDirection.Z
                ), false);
            }
        }
    }, [selectedKnowledgeView?.ID]);
    */
    const [assetSetsLoaded, setAssetSetsLoaded] = useState(false);
    const [assetSetPopulationVersion, setAssetSetPopulationVersion] = useState(0);
    const [assetSetMembershipRevision, setAssetSetMembershipRevision] = useState(0);
    const handleDisplayPopulationRetry = React.useCallback((assetSetID: string) => {
        ViewerActions.invalidateAssetSetMembership(assetSetID);
        setAssetSetMembershipRevision(revision => revision + 1);
    }, []);
    const [viewerInitialized, setViewerInitialized] = useState(false);
    const advancedMaterialTunings = React.useMemo(
        () => selectedKnowledgeView?.DisplayProperties?.AdvancedMaterialTunings ?? {},
        [selectedKnowledgeView?.DisplayProperties?.AdvancedMaterialTunings]
    );

    useEffect(() => {
        if (!viewerInitialized) return;
        ReactViewer3DActions.setAdvancedMaterialTunings(
            viewerId.current,
            advancedMaterialTunings
        );
    }, [viewerInitialized, selectedKnowledgeView?.ID, advancedMaterialTunings]);

    const displayAssetSetDefinitions = React.useMemo(
        () => buildAssetSetDisplayDefinitions(
            visualizationConfiguration,
            new Map(allFacilityAssetSets.map(assetSet => [normalizeDisplayID(assetSet.ID), assetSet]))
        ),
        [visualizationConfiguration, allFacilityAssetSets]
    );
    const isKnowledgeBookManageMode = shouldUseKnowledgeBookAssetSetManageMode(
        openedKnowledgeBook?.ID,
        knowledgeBookViewingMode,
        selectedAssetSet,
        assetSets
    );

    const manageAssetSetDefinitions = React.useMemo<IAssetSetDisplayDefinition[]>(() => {
        if (!selectedAssetSet?.ID) {
            return [];
        }

        return [{
            id: selectedAssetSet.ID,
            // MaterialOverrideManager gives lower priorities precedence,
            // while AssetSetManager reserves 0 for disabled sets.
            priority: 0.1,
            mode: 'color',
            colorKey: GOLDEN_DISPLAY_COLOR,
            tagIDs: selectedAssetSet.AssetTagIDs ?? [],
            isAll3DAssets: selectedAssetSet.Type === AssetSetType.All3DAssets,
            isAllAssetTags: selectedAssetSet.Type === AssetSetType.AllAssetTags,
        }];
    }, [selectedAssetSet]);

    // Display Priority remains active while its editor is closed.
    const activeFacilityMode: 'manage' | 'display' = facilityAssetSetsBarOpen
        ? 'manage'
        : 'display';
    const isAssetSetManageMode = isKnowledgeBookManageMode || activeFacilityMode === 'manage';
    const canModifySelectedAssetSet = canModifyAssetSetMembership(
        isAssetSetManageMode,
        selectedAssetSet,
        AssetSetType.List
    );

    const allAssetSetForVisibility = React.useMemo(() => {
        const sourceAssetSets = isKnowledgeBookManageMode || (isAssetSetManageMode && selectedAssetSet?.KnowledgeBookID)
            ? assetSets
            : allFacilityAssetSets;

        return sourceAssetSets.find(assetSet => assetSet.Type === AssetSetType.All3DAssets) ?? null;
    }, [
        selectedAssetSet?.KnowledgeBookID,
        isKnowledgeBookManageMode,
        isAssetSetManageMode,
        assetSets,
        allFacilityAssetSets,
    ]);

    const allAssetSetVisibilityDefinition = React.useMemo<IAssetSetDisplayDefinition[]>(() => (
        allAssetSetForVisibility?.ID
            ? [{
                id: allAssetSetForVisibility.ID,
                priority: Number.MAX_SAFE_INTEGER,
                mode: 'default',
                colorKey: undefined,
                tagIDs: [],
                isAll3DAssets: true,
                isAllAssetTags: false,
            }]
            : []
    ), [allAssetSetForVisibility?.ID]);

    const activeFacilityAssetSetDefinitions = React.useMemo(
        () => isAssetSetManageMode
            ? [
                ...manageAssetSetDefinitions,
                ...(selectedAssetSet?.Type === AssetSetType.All3DAssets
                    ? []
                    : allAssetSetVisibilityDefinition)
            ]
            : displayAssetSetDefinitions,
        [
            isAssetSetManageMode,
            manageAssetSetDefinitions,
            allAssetSetForVisibility?.ID,
            allAssetSetVisibilityDefinition,
            selectedAssetSet?.ID,
            selectedAssetSet?.Type,
            displayAssetSetDefinitions,
        ]
    );

    const selectedKnowledgeViewAssetSetIDs = React.useMemo(
        () => selectedKnowledgeView && !isAssetSetManageMode
            ? (selectedKnowledgeView.AssetSets ?? []).map(assetSet => assetSet.ID)
            : null,
        [selectedKnowledgeView?.ID, selectedKnowledgeView?.AssetSets, isAssetSetManageMode]
    );

    const effectiveAssetSets = React.useMemo(
        () => {
            let resolvedAssetSets = resolveViewerAssetSets({
                knowledgeViewAssetSetIDs: selectedKnowledgeViewAssetSetIDs,
                knowledgeBookAssetSets: assetSets,
                facilityAssetSets: allFacilityAssetSets,
                selectedAssetSet,
                knowledgeBookManageMode: isKnowledgeBookManageMode,
                facilityMode: activeFacilityMode,
                displayAssetSetIDs: visualizationConfiguration?.Entries.map(entry => entry.AssetSetID) ?? [],
            });

            if (isAssetSetManageMode &&
                selectedAssetSet?.Type !== AssetSetType.All3DAssets &&
                allAssetSetForVisibility?.ID &&
                !resolvedAssetSets.some(assetSet => assetSet.ID === allAssetSetForVisibility.ID)) {
                resolvedAssetSets = [...resolvedAssetSets, allAssetSetForVisibility];
            }

            return resolvedAssetSets;
        },
        [
            selectedKnowledgeViewAssetSetIDs,
            assetSets,
            allFacilityAssetSets,
            facilityAssetSets,
            selectedAssetSet,
            isKnowledgeBookManageMode,
            activeFacilityMode,
            isAssetSetManageMode,
            allAssetSetForVisibility,
            selectedFacilityID,
            visualizationConfiguration,
        ]
    );
    const knowledgeViewAssetSetsReady = React.useMemo(() => {
        if (!selectedKnowledgeView || selectedKnowledgeViewAssetSetIDs === null) return true;
        return areViewerAssetSetsResolved(effectiveAssetSets, selectedKnowledgeViewAssetSetIDs);
    }, [
        selectedKnowledgeView,
        selectedKnowledgeViewAssetSetIDs,
        effectiveAssetSets,
    ]);
    const effectiveAssetSetSignature = React.useMemo(
        () => getAssetSetDefinitionSignature(effectiveAssetSets),
        [effectiveAssetSets]
    );
    const effectiveNonAllAssetSetSignature = React.useMemo(
        () => getAssetSetDefinitionSignature(
            effectiveAssetSets.filter(assetSet => assetSet.Type !== AssetSetType.All3DAssets && assetSet.Type !== AssetSetType.AllAssetTags)
        ),
        [effectiveAssetSets]
    );
    const effectiveAllAssetSetID = React.useMemo(
        () => effectiveAssetSets.find(assetSet => assetSet.Type === AssetSetType.All3DAssets)?.ID ?? null,
        [effectiveAssetSets]
    );
    useEffect(() => {
        if (!selectedFacilityID) {
            setAllFacilityAssetSets([]);
            AssetSetVisualizationStore.reset();
            return;
        }

        let cancelled = false;
        AssetSetsActions.refreshFacilityAssetSets(selectedFacilityID, false).then(sets => {
            if (!cancelled) {
                setAllFacilityAssetSets(sets ?? []);
                const facilitySets = (sets ?? []).filter(assetSet => !assetSet.KnowledgeBookID);
                const all3DAssets = facilitySets.find(assetSet => assetSet.Type === AssetSetType.All3DAssets);
                const allAssetTags = facilitySets.find(assetSet => assetSet.Type === AssetSetType.AllAssetTags);
                if (all3DAssets && allAssetTags) {
                    AssetSetVisualizationStore.initialize(selectedFacilityID, facilitySets, all3DAssets.ID, allAssetTags.ID);
                }
            }
        }).catch(error => console.error('Unable to load facility asset sets.', error));

        return () => { cancelled = true; };
    }, [selectedFacilityID]);

    useEffect(() => {
        const knowledgeViewAssetIsolationActive = !!selectedKnowledgeView && !isAssetSetManageMode;
        const resetKnowledgeViewAssetLayers =
            knowledgeViewAssetIsolationActiveRef.current &&
            !knowledgeViewAssetIsolationActive &&
            viewerInitialized;
        knowledgeViewAssetIsolationActiveRef.current = knowledgeViewAssetIsolationActive;

        if (!knowledgeViewAssetSetsReady) {
            setAssetSetsLoaded(false);
            return;
        }

        if (!viewerInitialized) {
            setAssetSetsLoaded(false);
            return;
        }

        let cancelled = false;
        setAssetSetsLoaded(false);
        const activeOrdinaryIDs = effectiveAssetSets
            .filter(assetSet => assetSet.Type !== AssetSetType.All3DAssets && assetSet.Type !== AssetSetType.AllAssetTags)
            .map(assetSet => assetSet.ID);
        const pendingOrdinaryIDs = ViewerActions.getPendingAssetSetPopulationIds(effectiveAssetSets);
        AssetSetVisualizationStore.retainPopulationStatuses(activeOrdinaryIDs);
        AssetSetVisualizationStore.setPopulationStatus(pendingOrdinaryIDs, 'pending');
        const failedPopulationIDs = new Set<string>();
        const populateEffectiveAssetSets = async () => {
            const result = await ViewerActions.populateAssetSets(effectiveAssetSets);
            for (const assetSetID of result?.failedAssetSetIds ?? []) {
                failedPopulationIDs.add(assetSetID);
            }
        };

        (async () => {
            if (resetKnowledgeViewAssetLayers) {
                ViewerActions.destroyAllAssetSets();

                const requestId = ReactViewer3DActions.refreshAssetSetOctrees(
                    viewerId.current,
                    [],
                    false
                );
                await ReactViewer3DActions.waitForViewerDataSetup(viewerId.current, requestId);
                if (cancelled) return;
                knowledgeViewAssetSetSetupRef.current = {
                    knowledgeViewID: null,
                    nonAllSignature: '',
                    allSetID: null,
                    allLayerMapped: false,
                };
            }

            if (selectedKnowledgeView && !isAssetSetManageMode && viewerInitialized && effectiveAssetSets.length > 0) {
                const previousSetup = knowledgeViewAssetSetSetupRef.current;
                const needsKnowledgeViewAssetSetSetup =
                    previousSetup.knowledgeViewID !== selectedKnowledgeView.ID ||
                    previousSetup.nonAllSignature !== effectiveNonAllAssetSetSignature ||
                    (!!effectiveAllAssetSetID &&
                        (previousSetup.allSetID !== effectiveAllAssetSetID ||
                            !previousSetup.allLayerMapped));

                if (needsKnowledgeViewAssetSetSetup) {
                    const requestId = ReactViewer3DActions.refreshAssetSetOctrees(
                        viewerId.current,
                        effectiveAssetSets,
                        true
                    );
                    await ReactViewer3DActions.waitForViewerDataSetup(viewerId.current, requestId);
                    if (cancelled) return;

                    await populateEffectiveAssetSets();
                    if (cancelled) return;

                    knowledgeViewAssetSetSetupRef.current = {
                        knowledgeViewID: selectedKnowledgeView.ID,
                        nonAllSignature: effectiveNonAllAssetSetSignature,
                        allSetID: effectiveAllAssetSetID,
                        allLayerMapped: !!effectiveAllAssetSetID,
                    };
                }
            } else if (selectedKnowledgeView && !isAssetSetManageMode && viewerInitialized) {
                const previousSetup = knowledgeViewAssetSetSetupRef.current;
                if (previousSetup.knowledgeViewID !== selectedKnowledgeView.ID ||
                    previousSetup.nonAllSignature !== effectiveNonAllAssetSetSignature) {
                    await populateEffectiveAssetSets();
                    if (cancelled) return;

                    knowledgeViewAssetSetSetupRef.current = {
                        knowledgeViewID: selectedKnowledgeView.ID,
                        nonAllSignature: effectiveNonAllAssetSetSignature,
                        allSetID: effectiveAllAssetSetID,
                        allLayerMapped: false,
                    };
                }
            } else {
                const requestId = ReactViewer3DActions.refreshAssetSetOctrees(
                    viewerId.current,
                    effectiveAssetSets,
                    false
                );
                await ReactViewer3DActions.waitForViewerDataSetup(viewerId.current, requestId);
                if (cancelled) return;
                await populateEffectiveAssetSets();
                if (cancelled) return;
            }

            setAssetSetsLoaded(true);
            setAssetSetPopulationVersion(version => version + 1);
            AssetSetVisualizationStore.setPopulationStatus(
                activeOrdinaryIDs.filter(assetSetID => !failedPopulationIDs.has(assetSetID)),
                'ready'
            );
            AssetSetVisualizationStore.setPopulationStatus(Array.from(failedPopulationIDs), 'error');
        })().catch(error => {
            if (cancelled) return;
            AssetSetVisualizationStore.setPopulationStatus(pendingOrdinaryIDs, 'error');
            setAssetSetsLoaded(true);
            console.error('Unable to populate Display Priority Asset Sets.', error);
        });

        return () => { cancelled = true; };
    }, [
        effectiveAssetSetSignature,
        assetSetMembershipRevision,
        knowledgeViewAssetSetsReady,
        selectedKnowledgeView,
        selectedKnowledgeViewAssetSetIDs,
        isKnowledgeBookManageMode,
        isAssetSetManageMode,
        facilityAssetSetsBarOpen,
        selectedFacilityID,
        viewerInitialized,
        effectiveNonAllAssetSetSignature,
        effectiveAllAssetSetID,
    ]);

    const knowledgeViewCameraRestoreGenerationRef = React.useRef(0);
    const isKnowledgeViewCameraRestoreInProgressRef = React.useRef(true);
    // Reset reopens the same saved view, so its camera coordinates may be unchanged.
    const knowledgeViewOpenVersion = ViewerStore.getKnowledgeViewOpenVersion();

    const knowledgeViewCameraSignature = React.useMemo(
        () => selectedKnowledgeView
            ? `${selectedKnowledgeView.ID ?? ''}|${sigDisplayProps(selectedKnowledgeView)}`
            : '',
        [
            selectedKnowledgeView?.ID,
            selectedKnowledgeView?.DisplayProperties,
        ]
    );

    React.useLayoutEffect(() => {
        const restoreGeneration = ++knowledgeViewCameraRestoreGenerationRef.current;
        const cameraPosition =
            selectedKnowledgeView?.DisplayProperties?.CameraPosition;
        const cameraTarget =
            selectedKnowledgeView?.DisplayProperties?.CameraTarget;
        if (!cameraPosition || !cameraTarget) {
            isKnowledgeViewCameraRestoreInProgressRef.current = !selectedKnowledgeView;
            return;
        }

        isKnowledgeViewCameraRestoreInProgressRef.current = true;
        ReactViewer3DActions.setCamera(
            viewerId.current,
            new BABYLON.Vector3(
                cameraPosition.X,
                cameraPosition.Y,
                cameraPosition.Z
            ),
            new BABYLON.Vector3(
                cameraTarget.X,
                cameraTarget.Y,
                cameraTarget.Z
            ),
            false,
            () => {
                if (restoreGeneration !== knowledgeViewCameraRestoreGenerationRef.current) return;
                isKnowledgeViewCameraRestoreInProgressRef.current = false;
                ViewerActions.resetCameraAsDirty();
            }
        );
    }, [knowledgeViewCameraSignature, knowledgeViewOpenVersion]);

    const viewerGeometryContextSignature = getViewerGeometryContextSignature(
        selectedOntology?.ID,
        selectedOntology?.FacilityID,
        rangePosition,
        ViewerStore.getLODDistanceThreshold()
    );
    const previousGeometryOntologyIDRef = React.useRef(selectedOntology?.ID);

    useUpdateEffect(() => {
        const ontologyChanged =
            previousGeometryOntologyIDRef.current !== selectedOntology?.ID;
        previousGeometryOntologyIDRef.current = selectedOntology?.ID;

        // Opening a Knowledge View clears range state in ViewerStore. That is
        // a presentation change, not a reason to rebuild the facility octree.
        if (selectedKnowledgeView && !ontologyChanged) return;

        ReactViewer3DActions.reinitializeViewer(
            viewerId.current,
            FacilityStore.getSelectedOntology().ID,
            FacilityStore.getSelectedOntology().FacilityID,
            ontologyBoundsContext,
            rangePosition,
            ViewerStore.getLODDistanceThreshold(),
            [],
            null, null, null,
            false
        );
    }, [viewerGeometryContextSignature]);

    useEffect(() => {
        ViewerStore.addChangeListener(handleViewerStoreChange);
        FacilityStore.addChangeListener(handleFacilityStoreChange);
        KnowledgeBookStore.addChangeListener(handleKnowledgeBookStoreChange);
        AssetSetVisualizationStore.addChangeListener(handleAssetSetVisualizationStoreChange);
        AssetSetVisualizationStore.addAssetSetsChangeListener(handleVisualizationAssetSetsChange);
        AssetSetVisualizationStore.addPopulationRetryListener(handleDisplayPopulationRetry);
        handleFacilityStoreChange();

        return () => {
            ViewerStore.removeChangeListener(handleViewerStoreChange);
            FacilityStore.removeChangeListener(handleFacilityStoreChange);
            KnowledgeBookStore.removeChangeListener(handleKnowledgeBookStoreChange);
            AssetSetVisualizationStore.removeChangeListener(handleAssetSetVisualizationStoreChange);
            AssetSetVisualizationStore.removeAssetSetsChangeListener(handleVisualizationAssetSetsChange);
            AssetSetVisualizationStore.removePopulationRetryListener(handleDisplayPopulationRetry);
        };
    }, [
        handleViewerStoreChange,
        handleFacilityStoreChange,
        handleKnowledgeBookStoreChange,
        handleAssetSetVisualizationStoreChange,
        handleVisualizationAssetSetsChange,
        handleDisplayPopulationRetry,
    ]);

    useUpdateEffect(() => {
        Promise.resolve().then(() => {
            ReactViewer3DActions.setScanPositions(viewerId.current, views);
        });
    }, [views]);

    useUpdateEffect(() => {
        setHighlightedView(null);
    }, [views]);

    const isSelecting = useRef(false);

    useUpdateEffect(() => {
        if (!isSelecting.current) {
            const selectedItem = selectedHierarchyItem?.ID ?? selectedAnnotation ?? selectedAssembly;
            ReactViewer3DActions.highlightAssets(viewerId.current, selectedItem ? [selectedItem] : []);
        }
    }, [selectedAnnotation, selectedAssembly, selectedHierarchyItem]);

    useEffect(() => {
        ReactViewer3DActions.setMeasurements(viewerId.current, measurements);
    }, [measurements]);

    useEffect(() => {

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                ViewerActions.resetDisplayMode();
            } else if (event.key === 'Enter' && (displayMode === DisplayMode.TagVolumeMove || displayMode === DisplayMode.TagVolumeScale || displayMode === DisplayMode.AssemblyVolumeMove || displayMode === DisplayMode.AssemblyVolumeScale)) {
                ViewerActions.resetDisplayMode();
            }
        };
        const needsListener =
            assemblyCreationTemplate !== undefined ||
            measurementType !== null ||
            displayMode === DisplayMode.CreateAnnotation ||
            displayMode === DisplayMode.PositionAnnotation ||
            displayMode === DisplayMode.AssemblyVolumeMove ||
            displayMode === DisplayMode.AssemblyVolumeScale ||
            displayMode === DisplayMode.TagVolumeMove || 
            displayMode === DisplayMode.TagVolumeScale;

        if (needsListener) window.addEventListener('keydown', handleKeyDown);

        ReactViewer3DActions.setViewerClickMode(
            viewerId.current,
            displayMode === 'ann' || displayMode === 'pos' ? 'point3D' : 'select'
        );
        ReactViewer3DActions.setMeasurementType(viewerId.current, measurementType);
        ReactViewer3DActions.setAssemblyEditMode(
            viewerId.current,
            displayMode === DisplayMode.AssemblyVolumeMove ? 'move' :
                displayMode === DisplayMode.AssemblyVolumeScale ? 'scale' : 'none'
        );
        ReactViewer3DActions.setTagEditMode(
            viewerId.current,
            displayMode === DisplayMode.TagVolumeMove ? 'move' :
                displayMode === DisplayMode.TagVolumeScale ? 'scale' : 'none'
        );
        return () => { if (needsListener) window.removeEventListener('keydown', handleKeyDown); };
    }, [displayMode, measurementType, assemblyCreationTemplate]);

    useEffect(() => {
        if (focusedMeasurement) {
            let point: IVector3D; let centerPoint: any;
            if (focusedMeasurement.measureMode == MeasurementType.Point) {
                point = { X: (focusedMeasurement as any).startPosition.x, Y: (focusedMeasurement as any).startPosition.y, Z: (focusedMeasurement as any).startPosition.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.Distance) {
                centerPoint = (focusedMeasurement as any).startPosition.add((focusedMeasurement as any).endPosition.subtract((focusedMeasurement as any).startPosition).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.XOrtho) {
                const pB = new BABYLON.Vector3((focusedMeasurement as any).endPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);
                const pA = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);
                centerPoint = pA.add(pB.subtract(pA).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.YOrtho) {
                const pB = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).endPosition.y, (focusedMeasurement as any).startPosition.z);
                const pA = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);
                centerPoint = pA.add(pB.subtract(pA).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.ZOrtho) {
                const pB = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).endPosition.z);
                const pA = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);
                centerPoint = pA.add(pB.subtract(pA).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }
            ReactViewer3DActions.focusCamera(viewerId.current, new BABYLON.Vector3(point.X, point.Y, point.Z));
        }
    }, [focusedMeasurement]);

    useUpdateEffect(() => {

        if (focusedAnnotation?.Target3D) {


            if (focusedAnnotation?.CameraPosition) {
                ReactViewer3DActions.setCamera(viewerId.current,
                    new BABYLON.Vector3(focusedAnnotation.CameraPosition.X, focusedAnnotation.CameraPosition.Y, focusedAnnotation.CameraPosition.Z),
                    new BABYLON.Vector3(focusedAnnotation.Target3D.X, focusedAnnotation.Target3D.Y, focusedAnnotation.Target3D.Z),
                    false);
            }
            else {
                ReactViewer3DActions.zoomCamera(
                    viewerId.current,
                    {
                        X: (focusedAnnotation.Target3D.X - 2.5) * 1000,
                        Y: (focusedAnnotation.Target3D.Y - 2.5) * 1000,
                        Z: (focusedAnnotation.Target3D.Z - 2.5) * 1000
                    },
                    {
                        X: (focusedAnnotation.Target3D.X + 2.5) * 1000,
                        Y: (focusedAnnotation.Target3D.Y + 2.5) * 1000,
                        Z: (focusedAnnotation.Target3D.Z + 2.5) * 1000
                    }
                );
            }
        }
    }, [focusedAnnotation]);

    useUpdateEffect(() => {
        if (focusedHierarchyItem) {
            ViewerActions.getHierarchyItemExtents(focusedHierarchyItem.id).then((extent) => {
                // Check if the extent is valid
                if (extent.MinExtents.X <= extent.MaxExtents.X) {

                    const min = {
                        X: extent.MinExtents.X,
                        Z: extent.MinExtents.Y,
                        Y: extent.MinExtents.Z
                    };

                    const max = {
                        X: extent.MaxExtents.X,
                        Z: extent.MaxExtents.Y,
                        Y: extent.MaxExtents.Z
                    };

                    ReactViewer3DActions.zoomCamera(viewerId.current, min, max);
                }
            });
        }
    }, [focusedHierarchyItem]);

    useUpdateEffect(() => {
        ReactViewer3DActions.setAssemblyCreationTemplate(viewerId.current, assemblyCreationTemplate);
    }, [assemblyCreationTemplate]);

    useUpdateEffect(() => {
        updateAssemblyTemplatesIn3DViewer(assemblyTemplates);
    }, [assemblyTemplates]);

    useUpdateEffect(() => {
        updateAssembliesIn3DViewer(assemblies);
    }, [assemblies]);

    useUpdateEffect(() => {
        ReactViewer3DActions.setViewerConfiguration(viewerId.current, viewerConfig);
    }, [viewerConfig]);

    useEffect(() => {
        ReactViewer3DActions.setShowOccludedTags(viewerId.current, displayObstructedTags);
    }, [displayObstructedTags, isAssetSetManageMode, viewerInitialized]);

    useEffect(() => {
        let link: string = null;
        if (selectedAsset) {
            const asset = FacilityStore.getAssetById(selectedAsset);
            if (asset) {
                const nativeAsset = FacilityStore.getFacilityAssetsData().NativeAssets.find(a => a.AssetID == selectedAsset && a.Source === AssetSourceType.DocX);
                if (nativeAsset?.DocumentUrl) link = nativeAsset.DocumentUrl;
            }
        } else if (selectedHierarchyItem) {
            const nativeAsset = FacilityStore.getFacilityAssetsData().NativeAssets.find(a => a.AssetID == selectedHierarchyItem?.AssetID && a.Source === AssetSourceType.DocX);
                if (nativeAsset?.DocumentUrl) link = nativeAsset.DocumentUrl;
        }

        setDocumentLink(link);
    }, [selectedAsset, selectedHierarchyItem]);

    const dispatchViewerResize = React.useCallback(() => {
        addTooltipForTruncatedText();
        window.dispatchEvent(new Event('resize'));
    }, []);

    const clearScheduledViewerResizes = React.useCallback(() => {
        pendingResizeTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
        pendingResizeTimeoutsRef.current = [];
    }, []);

    const scheduleViewerResize = React.useCallback(() => {
        clearScheduledViewerResizes();

        dispatchViewerResize();

        requestAnimationFrame(() => {
            dispatchViewerResize();
            requestAnimationFrame(() => {
                dispatchViewerResize();
            });
        });

        [120, 250].forEach((delay) => {
            const timeoutId = window.setTimeout(() => {
                dispatchViewerResize();
            }, delay);
            pendingResizeTimeoutsRef.current.push(timeoutId);
        });
    }, [clearScheduledViewerResizes, dispatchViewerResize]);

    // --- Stable EJ2 resize handlers (IMPORTANT) ---
    const onOuterSplitResize = React.useCallback(() => {
        dispatchViewerResize();
    }, [dispatchViewerResize]);

    const onPropertySplitResize = React.useCallback(() => {
        // no-op but stable; prevents EJ2 from toggling undefined/function
    }, []);

    // If show/hide of the panel changes, schedule a resize once
    useEffect(() => {
        scheduleViewerResize();

        return () => {
            clearScheduledViewerResizes();
        };
    }, [showSplitter, scheduleViewerResize, clearScheduledViewerResizes]);

    useEffect(() => {
        if (!panoContainerRef.current || typeof ResizeObserver === 'undefined') {
            return;
        }

        let lastWidth = Math.round(panoContainerRef.current.getBoundingClientRect().width);
        let lastHeight = Math.round(panoContainerRef.current.getBoundingClientRect().height);

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) {
                return;
            }

            const nextWidth = Math.round(entry.contentRect.width);
            const nextHeight = Math.round(entry.contentRect.height);
            if (nextWidth === lastWidth && nextHeight === lastHeight) {
                return;
            }

            lastWidth = nextWidth;
            lastHeight = nextHeight;
            scheduleViewerResize();
        });

        resizeObserver.observe(panoContainerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [scheduleViewerResize]);

    function getSiteMap() {
        const ontology = FacilityStore.getSelectedOntology() ?? FacilityStore.getRootOntologyPart();
        return ontology?.Map;
    }

    function sigDisplayProps(kv: IKnowledgeViewDetail | null | undefined): string {
        const dp = kv?.DisplayProperties;
        if (!dp) return '';
        const cp = dp.CameraPosition;
        const ct = dp.CameraTarget;

        // include whatever DisplayProperties should count as "camera change"
        return [
            cp ? `${cp.X},${cp.Y},${cp.Z}` : '',
            ct ? `${ct.X},${ct.Y},${ct.Z}` : ''
        ].join('|');
    }

    const addTooltipForTruncatedText = () => {
        setTimeout(() => {
            const elements = document.getElementsByClassName('single-line-ellipsis');
            for (let i = 0; i < elements.length; i++) {
                const div: any = elements[i];
                if (div) {
                    if (div.scrollWidth > div.clientWidth) div.title = div.textContent.trim();
                    else div.removeAttribute('title');
                }
            }
        }, 10);
    };

    const updateDisplayedAnnotationsIn3DViewer = React.useCallback(() => {
        if (!assetSetsLoaded) return;

        let displayedAssetTags = AssetActions.filterTagsBySelectedOntology(allAssetTags, selectedOntology?.ID)
            .filter(tag => !filteredAssets.includes(tag.AssetID));
        displayedAssetTags = ViewerHelpers.filterAssetTagsByVisibleClasses(displayedAssetTags, visibleAssetTagClasses, FacilityStore.getSelectedFacility());
        let knowledgeViewTagResolution: ReturnType<typeof ViewerHelpers.resolveKnowledgeViewAssetTags> | null = null;
        let facilityDisplayTagResolution: ReturnType<typeof ViewerHelpers.resolveAssetSetDisplayTags> | null = null;
        if (selectedKnowledgeView && !isAssetSetManageMode) {
            knowledgeViewTagResolution = ViewerHelpers.resolveKnowledgeViewAssetTags(
                selectedKnowledgeView.AssetSets,
                effectiveAssetSets,
                displayedAssetTags.map(tag => tag.ID)
            );
            const visibleKnowledgeViewTagIDs = new Set(knowledgeViewTagResolution.visibleTagIDs);
            const claimedTagIDs = new Set([
                ...knowledgeViewTagResolution.visibleTagIDs,
                ...knowledgeViewTagResolution.hiddenTagIDs,
            ]);
            displayedAssetTags = displayedAssetTags.filter(tag =>
                visibleKnowledgeViewTagIDs.has(tag.ID) ||
                (knowledgeViewTagResolution!.fallbackVisible && !claimedTagIDs.has(tag.ID)));
        } else if (!isAssetSetManageMode) {
            facilityDisplayTagResolution = ViewerHelpers.resolveAssetSetDisplayTags(displayAssetSetDefinitions);
            const hiddenTagIDs = new Set(facilityDisplayTagResolution.hiddenTagIDs);
            const claimedTagIDs = new Set(Object.values(facilityDisplayTagResolution.tagIDsByAssetSetID).flat());
            displayedAssetTags = displayedAssetTags.filter(tag =>
                !hiddenTagIDs.has(tag.ID) &&
                (!facilityDisplayTagResolution!.fallbackHidden || claimedTagIDs.has(tag.ID)));
        }
        const viewerTags: IAnnotationDisplayItem[] = AssetActions.getAssetTagsForViewer(displayedAssetTags);
        const displayedKVAnnotations: IAnnotationDisplayItem[] = kvTagsInfo.tags ? [...kvTagsInfo.tags] : [];
        const combinedAnnotations = displayedKVAnnotations.concat(viewerTags);

        ReactViewer3DActions.setAnnotations(viewerId.current, combinedAnnotations);

        // Colorize after annotations are registered so Asset Tag colors can be
        // updated in place without rebuilding the 3D viewer.
        if (selectedKnowledgeView && !isAssetSetManageMode) {
            const kvSets = selectedKnowledgeView.AssetSets ?? [];
            const claimedTagIDs = new Set(Object.values(knowledgeViewTagResolution?.tagIDsByAssetSetID ?? {}).flat());
            const allAssetTagsID = effectiveAssetSets.find(assetSet => assetSet.Type === AssetSetType.AllAssetTags)?.ID;

            const combinedSets = kvSets.map(s => {
                return {
                    id: s.ID,
                    priority: s.Ord,
                    colorKey: s.Color,
                    hidden: !!s.Hidden,
                    materialID: s.MaterialID,
                    tagIDs: s.ID.toLocaleLowerCase() === allAssetTagsID?.toLocaleLowerCase()
                        ? displayedAssetTags.map(tag => tag.ID).filter(tagID => !claimedTagIDs.has(tagID))
                        : knowledgeViewTagResolution?.tagIDsByAssetSetID[s.ID] ?? []
                };
            });

            ViewerActions.colorizeAssetSets(combinedSets, true);
        } else if (!isAssetSetManageMode) {
            const claimedTagIDs = new Set(Object.values(facilityDisplayTagResolution?.tagIDsByAssetSetID ?? {}).flat());
            ViewerActions.colorizeAssetSets(
                activeFacilityAssetSetDefinitions.map(definition => ({
                    ...definition,
                    hidden: definition.mode === 'hidden',
                    tagIDs: definition.isAllAssetTags
                        ? displayedAssetTags.map(tag => tag.ID).filter(tagID => !claimedTagIDs.has(tagID))
                        : facilityDisplayTagResolution?.tagIDsByAssetSetID[definition.id] ?? [],
                })),
                false
            );
        } else {
            ViewerActions.colorizeAssetSets(activeFacilityAssetSetDefinitions, false);
        }

        // If an annotation selection exists, re-apply it on the newly displayed tags
        if (selectedAnnotation) {
            ReactViewer3DActions.highlightAssets(viewerId.current, [selectedAnnotation]);
        }
    }, [
        kvTagsInfo,
        assetSetsLoaded,
        assetSetPopulationVersion,
        allAssetTags,
        filteredAssets,
        effectiveAssetSets,
        selectedKnowledgeView?.ID,
        selectedKnowledgeView?.AssetSets,
        isAssetSetManageMode,
        selectedOntology?.ID,
        activeFacilityMode,
        activeFacilityAssetSetDefinitions,
        displayAssetSetDefinitions,
        facilityAssetSets,
        visibleAssetTagClasses
    ]);


    useUpdateEffect(() => {
        updateDisplayedAnnotationsIn3DViewer();
    }, [updateDisplayedAnnotationsIn3DViewer]);

    const updateAssemblyTemplatesIn3DViewer = (templates: IAssemblyTemplate[], callback?: () => void) => {
        ReactViewer3DActions.setAssemblyTemplates(viewerId.current, templates, callback);


    };
    const updateAssembliesIn3DViewer = (assemblies: IAssembly[]) => {
        ReactViewer3DActions.setAssemblies(viewerId.current, assemblies);
    };

    const hideScanContextMenu = React.useCallback(() => {
        setScanContextMenu(prev => prev.visible ? { visible: false, left: -1000, top: -1000, view: null } : prev);
    }, []);

    const closeAssetInformationPanel = () => {
        setShowSplitter(false);
        ViewerActions.changeAssetPanelVisibility(false);
    };
    const changePanelVisibility = () => {
        setShowSplitter(true);
        ViewerActions.changeAssetPanelVisibility(true);
    };

    const modeMap: Record<string, VolumeChangeMode> = {
        tvlm: 'tagmove',
        tvls: 'tagscale',
    };

    const handleVolumeTagChanged = async (tag: IAnnotationDisplayItem) => {
        const currentTag = allAssetTags.find(t => t.ID === tag.ID);
        if (!currentTag) return;

        const updatedTag = {
            ...currentTag,

            Text: tag.Text,
            Target2D: tag.Target2D,
            Target3D: tag.Target3D,
            TargetBoxMin: tag.TargetBoxMin,
            TargetBoxMax: tag.TargetBoxMax,
            AnnotationType: tag.AnnotationType
        };

        await ViewerActions.saveUpdatedAssetTag(updatedTag);
        setTimeout(() => {
            const mode = modeMap[displayMode] ?? '';
            if (mode) ViewerActions.setVolumeChangeDisplayMode(mode);
        }, 0);
    };

    const isAnnotationWithDetail = (annotation: IViewAnnotationTag) => {
        return annotation.AnnotationType == AnnotationType.InformationAnnotation
            || annotation.AnnotationType == AnnotationType.LinkAnnotation || annotation.AnnotationType == AnnotationType.ObservationTag;
    }
    useEffect(() => {
        if (selectedAnnotation) {
            const selectedKVTag = kvTagsInfo.tags?.find(tag => tag.ID == selectedAnnotation);
            if (selectedKVTag && isAnnotationWithDetail(selectedKVTag)) {
                if (selectedKVTag.AnnotationType === AnnotationType.InformationAnnotation) {
                    //show the popup with the annotation detail (rich text control)
                    setIsAnnotationLinkDetailShow(false)
                    setIsAnnotationDetailShow(true)
                    setIsAnnotationTextShow(false)
                } else if (selectedKVTag.AnnotationType === AnnotationType.LinkAnnotation) {
                    setIsAnnotationDetailShow(false);
                    setIsAnnotationLinkDetailShow(true)
                    setIsAnnotationTextShow(false)
                } else if (selectedKVTag.AnnotationType === AnnotationType.ObservationTag) {
                    setIsAnnotationDetailShow(false);
                    setIsAnnotationLinkDetailShow(false)
                }
            }
            else {
                if (!ViewerStore.getAnnotationEditorMode()) {
                    //hide any existing annotation detail popup
                    setIsAnnotationDetailShow(false)
                    setIsAnnotationLinkDetailShow(false)
                    setIsAnnotationTextShow(false)
                }
            }
        } else {
            setIsAnnotationLinkDetailShow(false)
            setIsAnnotationDetailShow(false);
            setIsAnnotationTextShow(false)
            resetAnnotationEditorMode();
        }
    }, [selectedAnnotation])

    const resetAnnotationEditorMode = () => {
        setTimeout(() => {
            ViewerActions.setAnnotationEditorMode(false)
        }, 1)
    }

    const handleMultiSelection = async (
        objectIDs: string[],
        allSelectedIDs: Interfaces.Interfaces.ISelectedObjectInfo[],
        type: 'object' | 'scan' | 'smartmesh' | 'mesh' | 'pagedmesh' | 'annotation' | 'assembly',
        ev?: PointerEvent
    ): Promise<Interfaces.Interfaces.IMultiSelectionResult> => {
        if (objectIDs.length === 0) {
            return {
                selected: allSelectedIDs || [],
                types: Array.from(new Set((allSelectedIDs || []).map(x => x.objectType)))
            };
        }

        switch (type) {
            case 'annotation':
                return ViewerActions.selectAnnotations(objectIDs, allSelectedIDs, ev?.button !== 2);

            case 'object':
            case 'mesh':
            case 'smartmesh': {
                const selectParent = !(ev?.ctrlKey);
                return await ViewerActions.selectHierarchyItems(objectIDs, allSelectedIDs, type, selectParent, true, ev?.button !== 2);
            }

            case 'pagedmesh': {
                const localSelection = objectIDs
                    .filter(id => !!id)
                    .map(id => ({ id, objectType: Enumerations.PickObjectType.PagedMesh }));

                if (localSelection.length > 0) {
                    ViewerActions.selectMeshItem(localSelection[0].id);
                }

                return {
                    selected: localSelection,
                    types: localSelection.length > 0 ? [Enumerations.PickObjectType.PagedMesh] : []
                };
            }

            case 'scan':
            case 'assembly':
            default:
                return {
                    selected: [],
                    types: []
                };
        }
    };

    const handle3DViewerSelect = async (
        objectIDs: string[],
        allSelectedIDs: Interfaces.Interfaces.ISelectedObjectInfo[],
        isSelected: boolean,
        type: 'object' | 'scan' | 'smartmesh' | 'mesh' | 'pagedmesh' | 'annotation' | 'assembly',
        ev?: PointerEvent
    ) => {
        try {
            let showContextMenu = false;
            let showAssetTagMenu = false;
            let showAssemblyMenu = false;

            let showAssetContextMenu = false;
            isSelecting.current = true;
            let multiSelectionResult: Interfaces.Interfaces.IMultiSelectionResult | null = null;

            if (isSelected) {
                const itemID = objectIDs[0];
                if (type === 'mesh') {
                    ViewerActions.selectMeshItem(itemID ?? 'fakeid');
                }
                else if (type === 'smartmesh') {
                    ViewerActions.selectMeshItem(itemID ?? 'fakeid');
                }
                else if (type === 'pagedmesh') {
                    ViewerActions.selectMeshItem(itemID ?? 'fakeid');
                } else if (type === 'annotation') {
                    const isKVAnnotation = kvTagsInfo.tags?.find(ann => ann.ID == itemID);
                    if (isKVAnnotation) {
                        setLastAnnotationPosition({ x: ev.x, y: ev.y });
                        if (ev?.button === 2) {
                            showContextMenu = true;
                        }
                    } else {
                        const isAssetTag = allAssetTags.find(tag => tag.ID == itemID);
                        if (isAssetTag && ev?.button === 2) {
                            showAssetTagMenu = true;
                        }
                    }

                    if (allowMultiSelect) {
                        multiSelectionResult = await handleMultiSelection(objectIDs, allSelectedIDs, type, ev);
                    } else {
                        ViewerActions.selectAnnotation(itemID);
                    }
                } else if (type === 'assembly') {
                    const bounds: any = undefined;

                    if (allowMultiSelect) {
                        multiSelectionResult = await handleMultiSelection(objectIDs, allSelectedIDs, type, ev);
                    } else {
                        ViewerActions.selectAssembly(itemID, bounds);

                        if (ev?.button === 2) {
                            showAssemblyMenu = true;
                        }
                    }
                }
                else if (allowMultiSelect) {
                    if (ev?.button === 2) {
                        showContextMenu = true;
                    }

                    multiSelectionResult = await handleMultiSelection(objectIDs, allSelectedIDs, type, ev);
                }
                else {
                    if (ev?.button === 2) {
                        showAssetContextMenu = true;
                    }

                    const selectParent = !(ev?.ctrlKey);
                    const preserveCurrentSelection = shouldPreserveSelectionForContextClick(
                        objectIDs,
                        allSelectedIDs,
                        ev?.button
                    );
                    if (!preserveCurrentSelection
                        && (!showContextMenu || (selectedAssetSet != null && selectedAssetSet?.Type === AssetSetType.List))) {
                        await ViewerActions.selectHierarchyItem(
                            itemID,
                            selectParent,
                            true,
                            getRepeatedSelectionBehavior(ev?.button)
                        );
                    }
                }
            } else {
                await ViewerActions.resetUpdatedAssetTag();

                ViewerActions.resetAnnotation();
                ViewerActions.resetHierarchyItemSelection();
                ViewerActions.resetMeshItemSelection();
                ViewerActions.resetAssemblySelection();
            }

            if (!allowMultiSelect) {
                if (showAssemblyMenu && ev?.button === 2) {
                    setAssemblyContextMenu({ visible: true, left: ev.x, top: ev.y, tagId: objectIDs[0] });
                } else {
                    hideAssemblyContextMenu();
                }

                if (showContextMenu && ev && (hasModifyKBPermission || hasManageKBPermission)) {
                    setContextMenu(true);
                } else {
                    setContextMenu(false);
                }

                if (showAssetTagMenu && ev) {
                    ViewerActions.resetDisplayMode();
                    setAssetTagContextMenu({ visible: true, left: ev.x, top: ev.y, tagId: objectIDs[0], items: [] });
                } else {
                    hideAssetTagContextMenu();
                }

                if (showAssetContextMenu && ev && canModifySelectedAssetSet) {
                    ViewerActions.resetDisplayMode();
                    setAssetContextMenu({
                        visible: true,
                        left: ev.x,
                        top: ev.y,
                        hierarchyItemId: objectIDs[0]
                    });
                } else {
                    hideAssetContextMenu();
                }
            } else {
                hideAssetContextMenu();
                hideAssemblyContextMenu();
                setContextMenu(false);
                hideAssetTagContextMenu();

                if (ev?.button === 2 && multiSelectionResult) {
                    const selectedTypes = new Set(multiSelectionResult.types);
                    const selectedItems = multiSelectionResult.selected || [];

                    const hasAnnotation = selectedTypes.has(Enumerations.PickObjectType.Annotation);
                    const hasObjectLike =
                        selectedTypes.has(Enumerations.PickObjectType.Object) ||
                        selectedTypes.has(Enumerations.PickObjectType.Mesh) ||
                        selectedTypes.has(Enumerations.PickObjectType.PagedMesh) ||
                        selectedTypes.has(Enumerations.PickObjectType.GLBObject);

                    const hasNonAddableToAssetSet =
                        selectedTypes.has(Enumerations.PickObjectType.ScanPos) ||
                        selectedTypes.has(Enumerations.PickObjectType.Assembly) ||
                        selectedTypes.has(Enumerations.PickObjectType.Scan) ||
                        selectedTypes.has(Enumerations.PickObjectType.Unknown);

                    if (hasAnnotation && !hasObjectLike) {
                        ViewerActions.resetDisplayMode();

                        if (multiSelectionResult.selected.length == 1) {
                            setAssetTagContextMenu({
                                visible: true,
                                left: ev.x,
                                top: ev.y,
                                tagId: objectIDs[0],
                                items: multiSelectionResult.selected
                            });
                        } else if (canModifySelectedAssetSet) {
                            setMultiSelectContextMenu({
                                visible: true,
                                left: ev.x,
                                top: ev.y,
                                items: multiSelectionResult.selected,
                                mode: 'assetTags'
                            });
                        }
                    }
                    else if (!hasNonAddableToAssetSet && canModifySelectedAssetSet) {
                        setMultiSelectContextMenu({
                            visible: true,
                            left: ev.x,
                            top: ev.y,
                            items: multiSelectionResult.selected,
                            mode: 'objects'
                        });
                    }
                }
            }

        } finally {
            isSelecting.current = false;
        }
    };

    const handleSelectionModeChanged = async (enabled:boolean) => {
        ViewerActions.setMultipleSelectionEnabled(enabled);
    };

    const handleViewerPoint3DClicked = async (point3D: IVector3D, _screenPoint: IVector2D, cameraPosition: IVector3D) => {
        if (displayMode === DisplayMode.CreateAnnotation) {
            const annotationType = ViewerStore.getAnnotationType();
            if (annotationType === AnnotationType.AssetTag || annotationType === AnnotationType.AssetTagWithBox) {
                await ViewerActions.createTempAssetTag(point3D, cameraPosition);
            }
            else {
                setContextMenu(false);
                const annotationID = await ViewerActions.createAnnotation(point3D);
                let x = _screenPoint.X + 50;
                let y = _screenPoint.Y + 50;
                if (annotationType === AnnotationType.ObservationTag) {
                    y = y + 30;
                    setIsAnnotationTextShow(true);
                }
                setLastAnnotationPosition({ x: x, y: y });

                ViewerActions.selectAnnotation(annotationID);
                ViewerActions.setAnnotationEditorMode(true);
            }
        } else if (displayMode === DisplayMode.PositionAnnotation) {
            const selectedAnnotationId = ViewerStore.getSelectedAnnotation();
            if (selectedAnnotationId) {
                // Check if it's an asset tag
                const assetTag = allAssetTags.find(t => t.ID == selectedAnnotationId);
                if (assetTag) {
                    await ViewerActions.repositionAssetTag(assetTag, point3D, cameraPosition);
                } else {
                    // Check if it's a knowledge view annotation
                    const kvAnnotation = kvTagsInfo.tags?.find(t => t.ID == selectedAnnotationId);
                    if (kvAnnotation) {
                        await ViewerActions.repositionAnnotation(kvAnnotation, point3D);
                        ViewerActions.resetAnnotation();
                    }
                }
            }
        }
        ViewerActions.resetDisplayMode();
    };

    const handleViewerInitialized = async (): Promise<void> => {
        setViewerInitialized(true);

        // push current tags/templates into the 3D viewer
        updateDisplayedAnnotationsIn3DViewer();

        updateAssemblyTemplatesIn3DViewer(assemblyTemplates, async () => {


            await ViewerActions.createAssemblyInstances(
                assemblies
            );
        });

        // ensure scan positions are sent after the viewer is ready
        Promise.resolve().then(() => {
            ReactViewer3DActions.setScanPositions(viewerId.current, views || []);
        });
    };

    // Debounced camera change
    const degreesTimeoutRef = React.useRef<any>(null);
    const lastCamChangeRef = React.useRef<{ position: IVector3D; target: IVector3D; rotatedDegrees: number } | null>(null);
    const lastUserCameraInteractionAtRef = React.useRef(0);

    const recordUserCameraInteraction = () => {
        lastUserCameraInteractionAtRef.current = Date.now();
    };

    const handleCameraChanged = (position: IVector3D, target: IVector3D, rotatedDegrees: number) => {
        lastCamChangeRef.current = { position, target, rotatedDegrees };
        if (!degreesTimeoutRef.current) {
            degreesTimeoutRef.current = setTimeout(() => {
                degreesTimeoutRef.current = null;
                if (!ViewerStore.getAnnotationEditorMode()) {
                    if (isAnnotationDetailShow || isAnnotationLinkDetailShow || isAnnotationTextShow) {
                        if (isAnnotationDetailShow) setIsAnnotationDetailShow(false)
                        if (isAnnotationLinkDetailShow) setIsAnnotationLinkDetailShow(false)
                        ViewerActions.resetAnnotation();
                    }
                    setContextMenu(false);
                }

                const data = lastCamChangeRef.current;
                if (!data || !mountedRef.current) return;
                setDirectionDegrees(data.rotatedDegrees);
                setOverridePosition(data.position);

                ViewerActions.set3DCameraProperties(data.position, data.target);
                const activeKnowledgeView = ViewerStore.getSelectedKnowledgeView();
                if (FacilityStore.getKnowledgeViewEditBarOpen()
                    && activeKnowledgeView
                    && shouldMark3DCameraDirty(
                        activeKnowledgeView.DisplayProperties,
                        data.position,
                        data.target,
                        isKnowledgeViewCameraRestoreInProgressRef.current,
                        Date.now() - lastUserCameraInteractionAtRef.current <= USER_CAMERA_INTERACTION_WINDOW_MS,
                    )) {
                    ViewerActions.setCameraAsDirty();
                }
            }, 500);
        }
    };

    useEffect(() => {
        return () => {
            if (degreesTimeoutRef.current) {
                clearTimeout(degreesTimeoutRef.current);
                degreesTimeoutRef.current = null;
            }
        };
    }, []);

    const handleNavigationItemClick = async (_type: 'as' | 'mp', item: IIdentityItem) => {
        if (item) {
            await ViewerActions.selectHierarchyItem(item.ID);
        }
    };

    const navigateToView = async (view: IView360Detail) => {
        // Prevent navigation if there are unsaved knowledge view changes
        if (ViewerActions.checkForKnowledgeViewChanges()) return;

        ViewerActions.showPanoViewerImage(view);
    };

    const handleScanPositionHighlighted = (viewOrId: IView360Detail | string | null) => {
        if (!mountedRef.current) return;
        if (!viewOrId) { setHighlightedView(null); return; }
        const v = typeof viewOrId === 'string'
            ? (views?.find(x => x.ID === viewOrId) ?? null)
            : viewOrId;
        setHighlightedView(v);
    };

    const handleScanContextMenuItemClick = (itemId: string) => {
        if (itemId === 'open360') {
            navigateToView(scanContextMenu.view);
        }
        hideScanContextMenu();
    };

    const handleScanPositionRightClick = (viewOrId: IView360Detail | string | null, position?: { clientX: number; clientY: number } | null) => {
        if (!mountedRef.current) return;
        const resolvedView = typeof viewOrId === 'string'
            ? (views?.find(x => x.ID === viewOrId) ?? null)
            : viewOrId;
        if (!resolvedView) {
            hideScanContextMenu();
            return;
        }

        const container = document.getElementById('kvApp-viewer3d');
        const rect = container?.getBoundingClientRect();
        const fallbackX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
        const fallbackY = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;
        const clientX = position?.clientX ?? fallbackX;
        const clientY = position?.clientY ?? fallbackY;
        setScanContextMenu({ visible: true, left: clientX, top: clientY, view: resolvedView });
    };

    const updateAppTitle = () => {
        const headerBarTitleElement = document.getElementById("nav-app-title");
        if (headerBarTitleElement) {
            let title: string = "";
            if (selectedKnowledgeView?.ID) {
                title = selectedKnowledgeView.Name;
            }
            else if (openedKnowledgeBook?.ID) {
                title = `${openedKnowledgeBook.Name}${(knowledgeBookViewingMode === 'manageAssets') ? ' (Asset Set View)' : ''}`;
            }
            else {
                title = selectedOntology.Name;
            }
            headerBarTitleElement.textContent = title;
            headerBarTitleElement.setAttribute("title", title);
        }
    }

    useEffect(() => {
        const id = setTimeout(() => {
            updateAppTitle();
        }, 300); // Delay to ensure state updates are settled

        return () => {
            clearTimeout(id);
        }
    }, [selectedKnowledgeView, openedKnowledgeBook, knowledgeBookViewingMode, selectedOntology]);

    const handleAssemblyCreated = async (
        name: string,
        assemblyTemplate: string,
        position: IVector3D,
        orientation: IVector3D,
        instanceScale: IVector3D
    ): Promise<void> => {
        await ViewerActions.saveAssemblyInstance({
            Name: name,
            AssemblyTemplateID: assemblyTemplate,
            KnowledgeViewID: selectedKnowledgeView?.ID,
            Position: position,
            Orientation: orientation,
            InstanceScale: instanceScale,
            ID: null,
            VersionToken: null,
            FacilityID: null,
        });
        ViewerActions.resetDisplayMode();
    };

    const handleAssemblyUpdated = async (id: string, position: IVector3D, orientation: IVector3D, instanceScale: IVector3D) => {
        const index = assemblies.findIndex(assembly => assembly.ID === id);
        if (index !== -1) {
            const updatedAssembly = {
                ...assemblies[index],
                Position: position,
                Orientation: orientation,
                InstanceScale: instanceScale,
            };
            if (selectedAssembly) {
                await ViewerActions.saveAssembly(updatedAssembly);
            }
        }
    };

    const [assetContextMenu, setAssetContextMenu] = useState<{
        visible: boolean;
        left: number;
        top: number;
        hierarchyItemId: string | null;
    }>({
        visible: false,
        left: -1000,
        top: -1000,
        hierarchyItemId: null
    });

    const [multiSelectContextMenu, setMultiSelectContextMenu] = useState<{
        visible: boolean;
        left: number;
        top: number;
        items: Interfaces.Interfaces.ISelectedObjectInfo[];
        mode: 'assetTags' | 'objects';
    }>({
        visible: false,
        left: -1000,
        top: -1000,
        items: [],
        mode: 'objects'
    });

    const hideAssetContextMenu = React.useCallback(() => {
        setAssetContextMenu(prev =>
            prev.visible
                ? { visible: false, left: -1000, top: -1000, hierarchyItemId: null }
                : prev
        );
    }, []);

    const addToAssetSetLabel = React.useMemo(() => {
        return selectedAssetSet?.Name
            ? `${InovxLoader.t('facility:action.addToAssetSet')} '${selectedAssetSet.Name}'`
            : InovxLoader.t('facility:action.addToAssetSet');
    }, [selectedAssetSet?.Name]);

    const removeFromAssetSetLabel = React.useMemo(() => {
        return selectedAssetSet?.Name
            ? `${InovxLoader.t('facility:action.removeFromAssetSet')} '${selectedAssetSet.Name}'`
            : InovxLoader.t('facility:action.removeFromAssetSet');
    }, [selectedAssetSet?.Name]);

    const selectionContainsItemsInSelectedAssetSet = React.useMemo(() => {
        if (!selectedAssetSet) return false;

        const selectedItems = ViewerStore.getSelectedObjects();
        if (!selectedItems?.length) return false;

        const hierarchyIds = new Set(
            (selectedAssetSet.HierarchyItems ?? []).map(x => x.ID)
        );

        const assetTagIds = new Set(
            selectedAssetSet.AssetTagIDs ?? []
        );

        return selectedItems.some(item => {
            if (!item?.id) return false;

            switch (item.objectType) {
                case Enumerations.PickObjectType.Annotation:
                    return assetTagIds.has(item.id);

                case Enumerations.PickObjectType.Object:
                case Enumerations.PickObjectType.Mesh:
                case Enumerations.PickObjectType.PagedMesh:
                case Enumerations.PickObjectType.GLBObject:
                    return hierarchyIds.has(item.id);

                default:
                    return false;
            }
        });
    }, [selectedAssetSet, selectedAsset, selectedAnnotation, selectedHierarchyItem, selectedMeshItem, selectedAssembly]);

    const assetContextItems = React.useMemo(() => {
        const items: any[] = [];

        if (canModifySelectedAssetSet) {
            items.push({
                id: 'addToAssetSet',
                label: addToAssetSetLabel,
                iconClass: 'add-selection-icon'
            });

            if (selectionContainsItemsInSelectedAssetSet) {
                items.push({
                    id: 'removeFromAssetSet',
                    label: removeFromAssetSetLabel,
                    iconClass: 'remove-icon'
                });
            }
        }

        return items;
    }, [
        selectedAssetSet,
        canModifySelectedAssetSet,
        addToAssetSetLabel,
        removeFromAssetSetLabel,
        selectionContainsItemsInSelectedAssetSet
    ]);

    const hideMultiSelectContextMenu = React.useCallback(() => {
        setMultiSelectContextMenu(prev =>
            prev.visible
                ? { visible: false, left: -1000, top: -1000, items: [], mode: 'objects' }
                : prev
        );
    }, []);

    const addSelectedToSelectedAssetSet = async (
        items: Interfaces.Interfaces.ISelectedObjectInfo[]
    ) => {
        const selectedAssetSet = ViewerStore.getSelectedAssetSet();
        if (!canModifySelectedAssetSet || !selectedAssetSet || !items?.length) return;

        const hierarchyIds = [
            ...(selectedAssetSet.HierarchyItems ?? []).map(x => x.ID)
        ];

        const assetTagIds = [
            ...(selectedAssetSet.AssetTagIDs ?? [])
        ];

        for (const item of items) {
            if (!item?.id) continue;

            switch (item.objectType) {
                case Enumerations.PickObjectType.Annotation:
                    if (FacilityStore.getAssetTagById(item.id) && !assetTagIds.includes(item.id)) {
                        assetTagIds.push(item.id);
                    }
                    break;

                case Enumerations.PickObjectType.Object:
                case Enumerations.PickObjectType.Mesh:
                case Enumerations.PickObjectType.PagedMesh:
                case Enumerations.PickObjectType.GLBObject:
                    if (!hierarchyIds.includes(item.id)) {
                        hierarchyIds.push(item.id);
                    }
                    break;
            }
        }

        const updatedAssetSet = await AssetSetsActions.updateAssetSet({
            ...selectedAssetSet,
            HierarchyItems: hierarchyIds.map(id => ({ ID: id, Name: '' })),
            AssetTagIDs: assetTagIds,
        } as IAssetSet);

        if (updatedAssetSet) {
            ViewerActions.selectAssetSet(updatedAssetSet);
        }
    };

    const multiSelectContextItems = React.useMemo(() => {
        const items: any[] = [];

        if (!canModifySelectedAssetSet) {
            return items;
        }

        if (
            multiSelectContextMenu.mode === 'assetTags' ||
            multiSelectContextMenu.mode === 'objects'
        ) {
            items.push({
                id: 'addToAssetSet',
                label: addToAssetSetLabel,
                iconClass: 'add-selection-icon'
            });

            if (selectionContainsItemsInSelectedAssetSet) {
                items.push({
                    id: 'removeFromAssetSet',
                    label: removeFromAssetSetLabel,
                    iconClass: 'remove-icon'
                });
            }
        }

        return items;
    }, [
        multiSelectContextMenu.mode,
        selectedAssetSet,
        canModifySelectedAssetSet,
        addToAssetSetLabel,
        removeFromAssetSetLabel,
        selectionContainsItemsInSelectedAssetSet
    ]);

    const addAssets = async () => {
        const selectedAssetSet = ViewerStore.getSelectedAssetSet();
        if (!canModifySelectedAssetSet || !selectedAssetSet) return;

        const selectedItems = ViewerStore.getSelectedObjects();
        if (!selectedItems.length) return;

        const hierarchyIds = new Set(
            (selectedAssetSet.HierarchyItems ?? []).map(x => x.ID)
        );

        const assetTagIds = new Set(
            selectedAssetSet.AssetTagIDs ?? []
        );

        for (const item of selectedItems) {
            if (!item?.id) continue;

            switch (item.objectType) {
                case Enumerations.PickObjectType.Annotation:
                    if (FacilityStore.getAssetTagById(item.id)) {
                        assetTagIds.add(item.id);
                    }
                    break;

                case Enumerations.PickObjectType.Object:
                case Enumerations.PickObjectType.Mesh:
                case Enumerations.PickObjectType.PagedMesh:
                case Enumerations.PickObjectType.GLBObject:
                    hierarchyIds.add(item.id);
                    break;
            }
        }

        const updatedAssetSet = await AssetSetsActions.updateAssetSet({
            ...selectedAssetSet,
            HierarchyItems: Array.from(hierarchyIds).map(id => ({ ID: id, Name: '' })),
            AssetTagIDs: Array.from(assetTagIds),
        } as IAssetSet);

        if (updatedAssetSet) {
            ViewerActions.selectAssetSet(updatedAssetSet);
        }
    };

    const removeAssets = async () => {
        const selectedAssetSet = ViewerStore.getSelectedAssetSet();
        if (!canModifySelectedAssetSet || !selectedAssetSet) return;

        const selectedItems = ViewerStore.getSelectedObjects();
        if (!selectedItems.length) return;

        const hierarchyIdsToRemove = new Set<string>();
        const assetTagIdsToRemove = new Set<string>();

        for (const item of selectedItems) {
            if (!item?.id) continue;

            switch (item.objectType) {
                case Enumerations.PickObjectType.Annotation:
                    if (FacilityStore.getAssetTagById(item.id)) {
                        assetTagIdsToRemove.add(item.id);
                    }
                    break;

                case Enumerations.PickObjectType.Object:
                case Enumerations.PickObjectType.Mesh:
                case Enumerations.PickObjectType.PagedMesh:
                case Enumerations.PickObjectType.GLBObject:
                    hierarchyIdsToRemove.add(item.id);
                    break;
            }
        }

        const updatedAssetSet = await AssetSetsActions.updateAssetSet({
            ...selectedAssetSet,
            HierarchyItems: (selectedAssetSet.HierarchyItems ?? [])
                .filter(x => !hierarchyIdsToRemove.has(x.ID)),
            AssetTagIDs: (selectedAssetSet.AssetTagIDs ?? [])
                .filter(id => !assetTagIdsToRemove.has(id)),
        } as IAssetSet);

        if (updatedAssetSet) {
            ViewerActions.selectAssetSet(updatedAssetSet);
        }
    };

    const handleMultiSelectContextMenuItemClick = async (itemId: string) => {
        try {
            if (itemId === 'addToAssetSet') {
                await addAssets();
            } else if (itemId === 'removeFromAssetSet') {
                await removeAssets();
            }
        } finally {
            hideMultiSelectContextMenu();
        }
    };

    const handleSettingChanged = (name: string, value: string) => {
        setTimeout(() => {
            ViewerActions.updateViewerSettings(name, value);
        }, 0);
    };
    // Bounds / camera / threshold
    const ontologyBoundsContext: IOntologyBoundsContext = FacilityStore.getOntologyBoundsContext();

    const camPosition = ViewerStore.getViewerCameraProperties().position;
    const camYaw = ViewerStore.getViewerCameraProperties().yaw;
    const camPitch = ViewerStore.getViewerCameraProperties().pitch;
    const overrideFocalDistance = ViewerStore.getOverrideFocalDistance();
    const distanceThreshold = ViewerStore.getLODDistanceThreshold();
    const rPos = ViewerStore.getRangePosition();
    const displayText =
        (displayMode == DisplayMode.CreateAnnotation) ? InovxLoader.t('facility:message.pickTagLocation') :
            (displayMode == DisplayMode.PositionAnnotation) ? InovxLoader.t('facility:message.repositionTag') :
                (measurementType == MeasurementType.Point) ? InovxLoader.t('facility:message.pickPointLocation') :
                    (measurementType != null) ? InovxLoader.t('facility:message.pickDistanceLocation') :
                        (assemblyCreationTemplate != null) ? InovxLoader.t('facility:message.pickAssemblyLocation') :
                            (displayMode == DisplayMode.TagVolumeMove || displayMode == DisplayMode.TagVolumeScale || displayMode == DisplayMode.AssemblyVolumeMove || displayMode == DisplayMode.AssemblyVolumeScale) ? InovxLoader.t('facility:message.exitEditMode') : null;

    const ontologyReadOnly = !!openedKnowledgeBook?.OntologyID;
    const visibleMapViews = React.useMemo(() => {
        if (!contentLayers.positions360) return [];

        return views.filter(view => isScanPositionView(view)
            ? (isBlackAndWhiteScanPosition(view)
                ? contentLayers.blackAndWhiteScanPositions
                : contentLayers.colorScanPositions)
            : contentLayers.imagePositions);
    }, [views, contentLayers.positions360, contentLayers.colorScanPositions, contentLayers.blackAndWhiteScanPositions, contentLayers.imagePositions]);

    const handleMiniMapViewHighlighted = (view: IView360Detail, _index: number, highlighted: boolean) => {
        const v = highlighted ? view : null;
        setHighlightedView(v);
        ReactViewer3DActions.highlightScanPosition(viewerId.current, v ? v.ID : null);
    };

    const openAnnotation = () => {
        const selectedKVTag = kvTagsInfo.tags?.find(tag => tag.ID == selectedAnnotation);
        if (selectedKVTag && !ViewerStore.getAnnotationEditorMode()) {
            ViewerActions.setAnnotationEditorMode(true)
        }
        if (selectedKVTag.AnnotationType === AnnotationType.InformationAnnotation) {
            setIsAnnotationLinkDetailShow(false)
            setIsAnnotationTextShow(false)
            setIsAnnotationDetailShow(true);
        } else if (selectedKVTag.AnnotationType === AnnotationType.LinkAnnotation) {
            setIsAnnotationDetailShow(false);
            setIsAnnotationTextShow(false)
            setIsAnnotationLinkDetailShow(true)
        } else if (selectedKVTag.AnnotationType === AnnotationType.ObservationTag) {
            setIsAnnotationDetailShow(false);
            setIsAnnotationLinkDetailShow(false)
            setIsAnnotationTextShow(true)
        }
    }

    const deleteAnnotation = () => {
        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:message.confirmDeleteAnnotation'),
            confirmCallback: () => {
                setContextMenu(false);
                const selectedKVTag = kvTagsInfo.tags?.find(tag => tag.ID == selectedAnnotation);
                ViewerActions.deleteAnnotation(selectedKVTag);
                ViewerActions.resetAnnotation();
            }
        });
    }

    const handleAnnotationMenuAction = (type: 'edit' | 'delete' | 'reposition') => {
        if (type === 'delete') {
            deleteAnnotation();
        } else if (type === 'edit') {
            setContextMenu(false);
            openAnnotation();
        } else if (type === 'reposition') {
            setContextMenu(false);
            
            // Make sure annotation detail popups are closed
            setIsAnnotationDetailShow(false);
            setIsAnnotationLinkDetailShow(false);
            setIsAnnotationTextShow(false);
            
            ViewerActions.setPositionAnnotationDisplayMode();
        }
    };

    const scanContextMenuItems = React.useMemo(() => {
        const viewName = scanContextMenu?.view?.Name ?? '';

        return [
            {
                id: 'open360',
                label: `${InovxLoader.t('facility:action.openScanPosition')} '${viewName}'`,
                iconClass: 'focus-icon'
            }
        ];
    }, [scanContextMenu?.view]);

    const annotationContextItems = React.useMemo(() => {
        const items = [{ id: 'edit', label: InovxLoader.t('facility:action.editAnnotation'), iconClass: 'edit-icon' }];
        if(hasManageKBPermission) items.push({ id: 'delete', label: InovxLoader.t('facility:action.deleteAnnotation'), iconClass: 'delete-icon' });
        items.push({ id: 'reposition', label: InovxLoader.t('facility:action.repositionAnnotation'), iconClass: 'reposition-icon' });
        return items;
    },[]);
    

    const assetTagContextItems = React.useMemo(() => {
        const items = [];
        const facility = FacilityStore.getSelectedFacility();
        if (facility) {
            const selectedItem = facility?.AssetTags?.find((ann) => ann.ID === assetTagContextMenu.tagId) || null;
            if (selectedItem?.AnnotationType === AnnotationType.AssetTagWithBox) {
                items.push(
                    { id: 'move', label: InovxLoader.t('facility:action.move'), iconClass: 'move-icon' },
                    { id: 'resize', label: InovxLoader.t('facility:action.resize'), iconClass: 'resize-icon' }
                );
            }
            items.push({ id: 'edit', label: InovxLoader.t('facility:action.editAssetTag'), iconClass: 'edit-icon' });
            if (hasManageTagsPermission) {
                items.push({id: 'delete', label: InovxLoader.t('facility:action.deleteAssetTag'), iconClass: 'delete-icon'});
            }
            items.push({id: 'reposition', label: InovxLoader.t('facility:action.repositionAssetTag'),iconClass: 'reposition-icon'});

            if (canModifySelectedAssetSet) {
                items.push({
                    id: 'addToAssetSet',
                    label: addToAssetSetLabel,
                    iconClass: 'add-selection-icon'
                });

                if (selectionContainsItemsInSelectedAssetSet) {
                    items.push({
                        id: 'removeFromAssetSet',
                        label: removeFromAssetSetLabel,
                        iconClass: 'remove-icon'
                    });
                }
            }
        }
        return items;
    }, [
        hasManageTagsPermission,
        assetTagContextMenu.tagId,
        selectedAssetSet,
        canModifySelectedAssetSet,
        addToAssetSetLabel,
        removeFromAssetSetLabel,
        selectionContainsItemsInSelectedAssetSet
    ]);

    const hideAssetTagContextMenu = React.useCallback(() => {
        setAssetTagContextMenu(prev => prev.visible ? { visible: false, left: -1000, top: -1000, tagId: null, items: [] } : prev);
    }, []);

    const handleAssetTagContextMenuItemClick = async (itemId: string) => {
        const tagId = assetTagContextMenu.tagId;
        hideAssetTagContextMenu();
        if (!tagId) return;

        const tag = allAssetTags.find(t => t.ID === tagId);
        if (!tag) return;

        if (itemId === 'edit') {
            ViewerActions.editAssetTag(tag);
        } else if (itemId === 'delete') {
            ViewerActions.confirmDeleteAssetTag(tag);
        } else if (itemId === 'reposition') {
            ViewerActions.setPositionAnnotationDisplayMode();
        } else if (itemId === 'move') {
            ViewerActions.setVolumeChangeDisplayMode('tagmove');
        } else if (itemId === 'resize') {
            ViewerActions.setVolumeChangeDisplayMode('tagscale');
        } else if (itemId === 'addToAssetSet') {
            await addAssets();
        } else if (itemId === 'removeFromAssetSet') {
            await removeAssets();
        }
    };

    const assemblyContextItems = React.useMemo(() => {
        const assembly: IAssembly | undefined = assemblies.find((a: IAssembly) => a.ID === selectedAssembly);
        if (assembly) {
            const template = AssemblyLibraryStore.getAllAssemblyTemplates().find(t => t.ID === assembly.AssemblyTemplateID);

            const isDisabledResize =
                !template || !template.Scalable ? true : false;
            const items = [
                { id: 'delete', label: InovxLoader.t("facility:action.deleteAssembly"), iconClass: 'delete-icon' },
                { id: 'resize', label: InovxLoader.t('facility:action.resizeAssembly'), iconClass: 'resize-icon', isDisabled: isDisabledResize },
                { id: 'move', label: InovxLoader.t('facility:action.move&RotateAssembly'), iconClass: 'move-icon' },
                { id: 'edit', label: InovxLoader.t('facility:action.editProperties'), iconClass: 'edit-icon' }];
            return items;
        }
        return null;
    }, [selectedAssembly]);

    const isAssemblyContextMenuVisible = () => {
        if (ViewerStore.getSelectedKnowledgeView()) {
            return ComponentHelpers.hasModifyKnowledgeBookPermission(openedKnowledgeBook);
        } else {
            return ComponentHelpers.hasManageContentPermission();
        }
    }

    const hideAssemblyContextMenu = React.useCallback(() => {
        setAssemblyContextMenu(prev => prev.visible ? { visible: false, left: -1000, top: -1000, tagId: null } : prev);
    }, []);

    const deleteAssembly = () => {
        if (!selectedAssembly) return;
        const messageObject = {
            message: InovxLoader.t("facility:message.confirmDeleteAssembly"),
            confirmCallback: async () => {
                await ViewerActions.deleteAssembly(selectedAssembly);
            },
        };
        SystemActions.displayConfirmationMessage(messageObject);
    };

    const handleAssetContextMenuItemClick = async (itemId: string) => {
        try {
            if (itemId === 'addToAssetSet' && assetContextMenu.hierarchyItemId) {
                await addAssets();
            } else if (itemId === 'removeFromAssetSet' && assetContextMenu.hierarchyItemId) {
                await removeAssets();
            }
        } finally {
            hideAssetContextMenu();
        }
    };

    const handleAssemblyContextMenuItemClick = async (itemId: string) => {
        const tagId = assemblyContextMenu.tagId;
        hideAssemblyContextMenu();
        if (!tagId) return;

        const tag = assemblies.find(t => t.ID === tagId);
        if (!tag) return;

        if (itemId === 'edit') {
            const assembly: IAssembly | undefined = assemblies.find((a: IAssembly) => a.ID === selectedAssembly);
            if (assembly) {
                setSelectedAssemblyObj(assembly);
                setIsEditAssemblyModalVisible(true);
            }
        } else if (itemId === 'delete') {
            deleteAssembly();
        } else if (itemId === 'move') {
            ViewerActions.setVolumeChangeDisplayMode("assemblymove");
        } else if (itemId === 'resize') {
            ViewerActions.setVolumeChangeDisplayMode("assemblyscale");
        }
    };
    return (
            <div id="target" className="control-section splitter-expand" ref={viewerShellRef}>
            <div className="splitter-content-expand" id="PanoContainer" ref={panoContainerRef}>
                <MeasurementBar transientOnly={selectedKnowledgeView == null} />
                <AssemblyLibraryBar />
                <AssetTagsBar mode="3dviewer" />
                <AssetTagsForm />
                <div id="viewer-3d" className="kvApp-viewer-container minimap-container">
                    {hasOntologyHierarchy && !displayText && !selectedHierarchyItem && !openedKnowledgeBook && (
                        <OntologyDropDown selectedOntology={selectedOntology} readonly={ontologyReadOnly} />
                    )}
                    {displayText && (<div className="overlay-text">{displayText}</div>)}
                    {renderMiniMap && overridePosition && (
                        <FacilityMiniMap
                            siteMap={siteMap}
                            views={visibleMapViews}
                            title={facilityTitle}
                            targetId="#viewer-3d"
                            activeView={null}
                            highlightedView={highlightedView}
                            viewerDirectionDegrees={directionDegrees}
                            knowledgeViewOpened={false}
                            pointCloudAvailable={false}
                            onViewMarkerClicked={navigateToView}
                            onViewMarkerHighlighted={handleMiniMapViewHighlighted}
                            overridePosition={overridePosition}
                        />
                    )}
                    {selectedHierarchyItem && (
                        <NavigationPopup
                            selectedAsset={selectedHierarchyItem}
                            toolbar={null}
                            onAssetClick={(item) => { handleNavigationItemClick('as', item); }}
                        />
                    )}
                    <div
                        className="kvApp-viewer"
                        id="kvApp-viewer3d"
                        onKeyDownCapture={recordUserCameraInteraction}
                        onPointerDownCapture={recordUserCameraInteraction}
                        onWheelCapture={recordUserCameraInteraction}
                    >
                        <AssetInformationButton onShowAssetInformation={changePanelVisibility} />
                        <ContentLayersButton assetSets={effectiveAssetSets} />
                        <React3DViewer
                            viewerId={viewerId.current}
                            assetObjectListId={FacilityStore.getSelectedOntology().ID}
                            projectId={FacilityStore.getSelectedOntology().FacilityID}
                            ontologyBoundsContext={ontologyBoundsContext}
                            camPosition={camPosition}
                            camPitch={camPitch}
                            camYaw={camYaw}
                            camFocalDistance={overrideFocalDistance}
                            rangePosition={rPos}
                            distanceThreshold={distanceThreshold}
                            assetSets={effectiveAssetSets}
                            assetSetIsolation={selectedKnowledgeView != null && !isAssetSetManageMode}
                            namedLists={[]}
                            lodData={ViewerStore.getLODData()}
                            viewerConfig={viewerConfig}
                            onVolumeTagChanged={async (tag) => await handleVolumeTagChanged(tag)}
                            onViewerSelectionChanged={async (objectIDs, allSelectedIDs, isSelected, type, ev) => await handle3DViewerSelect(objectIDs, allSelectedIDs, isSelected, type, ev)}
                            onViewerSelectionModeChanged={async (enabled) => await handleSelectionModeChanged(enabled)}
                            onViewerPoint3DClicked={async (point3D, screenPoint, cameraPosition) => await handleViewerPoint3DClicked(point3D, screenPoint, cameraPosition)}
                            onViewerInitialized={async () => await handleViewerInitialized()}
                            onCameraChanged={async (position, target, rotatedDegrees) => await handleCameraChanged(position, target, rotatedDegrees)}
                            onScanPositionHighlighted={(viewOrId) => handleScanPositionHighlighted(viewOrId)}
                            onScanPositionRightClicked={(viewOrId, position) => handleScanPositionRightClick(viewOrId, position)}
                            onAssemblyCreated={(name, assemblyTemplate, position, orientation, instanceScale) => handleAssemblyCreated(name, assemblyTemplate, position, orientation, instanceScale)}
                            onAssemblyUpdated={(id, position, orientation, instanceScale) => handleAssemblyUpdated(id, position, orientation, instanceScale)}
                            onSettingsChanged={(name, value) => handleSettingChanged(name, value) }
                        />
                        {!contextMenu && isAnnotationLinkDetailShow && <AnnotationLinkEditor
                            onClose={() => setIsAnnotationLinkDetailShow(false)}
                            left={lastAnnotationPosition.x}
                            top={lastAnnotationPosition.y}
                            target="viewer-3d"
                            class="annotation-3d"
                        />}
                        {!contextMenu && isAnnotationDetailShow && <AnnotationTextEditor
                            onClose={() => setIsAnnotationDetailShow(false)}
                            left={lastAnnotationPosition.x}
                            top={lastAnnotationPosition.y}
                            target="viewer-3d"
                            class="annotation-3d"
                        />}
                        {!contextMenu && isAnnotationTextShow && ViewerStore.getAnnotationEditorMode() && <AnnotationEditor
                            onClose={() => setIsAnnotationTextShow(false)}
                            left={lastAnnotationPosition.x}
                            top={lastAnnotationPosition.y}
                            target="viewer-3d"
                        />}
                        {contextMenu && (
                            <ContextMenu
                                left={lastAnnotationPosition.x}
                                top={lastAnnotationPosition.y}
                                items={annotationContextItems}
                                onItemClick={(itemId) => {
                                    if (itemId === 'edit' || itemId === 'delete' || itemId === 'reposition') {
                                        handleAnnotationMenuAction(itemId);
                                    }
                                }}
                                containerId="annotation-context-menu"
                            />
                        )}
                        {multiSelectContextMenu.visible && (
                            <ContextMenu
                                left={multiSelectContextMenu.left}
                                top={multiSelectContextMenu.top}
                                items={multiSelectContextItems}
                                onItemClick={handleMultiSelectContextMenuItemClick}
                                onRequestClose={hideMultiSelectContextMenu}
                                containerId="multi-select-context-menu"
                            />
                        )}
                        {assetContextMenu.visible && (
                            <ContextMenu
                                left={assetContextMenu.left}
                                top={assetContextMenu.top}
                                items={assetContextItems}
                                onItemClick={handleAssetContextMenuItemClick}
                                onRequestClose={hideAssetContextMenu}
                                containerId="asset-context-menu"
                            />
                        )}
                        {scanContextMenu.visible && (
                            <ContextMenu
                                left={scanContextMenu.left}
                                top={scanContextMenu.top}
                                items={scanContextMenuItems}
                                onItemClick={handleScanContextMenuItemClick}
                                onRequestClose={hideScanContextMenu}
                                containerId="scan-position-context-menu"
                            />
                        )}
                        {(assetTagContextMenu.visible && (hasModifyTagsPermission || hasManageTagsPermission)) && (
                            <ContextMenu
                                left={assetTagContextMenu.left}
                                top={assetTagContextMenu.top}
                                items={assetTagContextItems}
                                onItemClick={handleAssetTagContextMenuItemClick}
                                onRequestClose={hideAssetTagContextMenu}
                                containerId="asset-tag-context-menu"
                            />
                        )}
                        {(assemblyContextMenu.visible && isAssemblyContextMenuVisible()) && (
                            <ContextMenu
                                left={assemblyContextMenu.left}
                                top={assemblyContextMenu.top}
                                items={assemblyContextItems}
                                onItemClick={handleAssemblyContextMenuItemClick}
                                onRequestClose={hideAssemblyContextMenu}
                                containerId="assembly-context-menu"
                            />
                        )}
                        {isEditAssemblyModalVisible && <EditAssemblyForm assembly={selectedAssemblyObj} onClose={() => setIsEditAssemblyModalVisible(false)} />}
                    </div>
                </div>
            </div>

            <div id="PropertyPanel" className="h-100p">
                <div id="AssetInfoContainer" className="h-100p">
                    <AssetInformation isVisible={showSplitter} onClose={closeAssetInformationPanel} />
                </div>
                <div id="AssetDocContainer" className="h-100p">
                    {showSplitter && documentLink && <iframe src={documentLink} className="document-iframe" title={InovxLoader.t('facility:viewer.embeddedDocument')} />}
                </div>
                <SplitterComponent
                    separatorSize={4}
                    orientation="Vertical"
                    ref={propertySplitterInstance}
                    resizing={onPropertySplitResize}   // <- stable no-op (important)
                    resizeStop={scheduleViewerResize}
                >
                    <PanesDirective>
                        <PaneDirective collapsible={true} size={documentLink ? '60%' : '100%'} content="#AssetInfoContainer" />
                        <PaneDirective collapsible={true} content="#AssetDocContainer" />
                    </PanesDirective>
                </SplitterComponent>
            </div>

            <SplitterComponent
                resizing={onOuterSplitResize}        // <- stable callback (important)
                resizeStop={scheduleViewerResize}
                created={scheduleViewerResize}
                height="100%"
                width="100%"
                separatorSize={4}
                ref={outerSplitterInstance}
            >
                <PanesDirective>
                    <PaneDirective size="75%" min="300px" content="#PanoContainer" />
                    <PaneDirective size="25%" min="300px" content="#PropertyPanel" collapsed={!showSplitter} />
                </PanesDirective>
            </SplitterComponent>

        </div>
    );
});

export default LODViewer;
